import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button.js';
import { Checkbox } from '../components/ui/checkbox.js';
import { useCustomer } from '../customer-context.js';
import { useContent } from '../content.js';
import { api } from '../api/client.js';
import {
  ACTION_DENIED_MESSAGE,
  CONNECTIVITY_OR_OFFLINE_MESSAGE,
  OTP_LOCKED_OUT_MESSAGE,
  isActionForbidden,
  isOnlineOnlyOfflineDenial,
  isOtpLockedOut,
} from '../lib/action-denied.js';
import {
  logSelfServeTransferFailure,
  selfServeTransferErrorMessage,
} from '../lib/self-serve-transfer-error.js';
import {
  REGISTRATION_SKIP_2FA_REASONS,
  CUSTOMER_DEVICE_REASON,
  requiresTroubleshootingConfirmation,
} from '../lib/skip-2fa-reasons.js';
import {
  beginRegistrationFail2fa,
  beginRegistrationSecurity,
  checkRegistration2faStatus,
  clearRegistrationFail2fa,
  completeRegistrationForIpw,
  completeRegistrationSecurity,
  recordRegistrationFail2fa,
  recordRegistrationSkip2fa,
} from '../lib/registration-security.js';
import {
  RESET_INTERNET_PASSWORD_ROUTE,
  clearRegistrationAnswers,
  peekRegistrationAnswers,
  saveRegistrationAnswers,
} from '../lib/registration-ipw.js';
import { AuthHoldPopup } from './AuthHoldPopup.js';
import { useAuthHoldCheck } from '../lib/use-auth-hold-check.js';
import infoIcon from '../assets/info-icon.svg';

/**
 * Shared building blocks for the Registration screens (SCRN-17..21 / CCF-57/58/
 * 59/60 + the Open24/EDS registration). Each registration screen is a thin
 * configuration over {@link RegistrationScreen}: it declares its heading, the
 * mandatory identity items, the additional security questions (and how many the
 * agent must answer), whether a 2FA OTP step applies, and which transfer actions
 * are offered. The shared component renders the Figma-faithful Pass/Fail tiers,
 * the mandatory scripting, the 2FA step (reusing the existing
 * `/security/otp/*` BFF routes), and the transfer/manual-password actions.
 *
 * The screens are reached after a deregistration (CCF-8/CCF-46 routes the agent
 * here by online-card type) OR directly from navigation. They read the customer
 * identity-question data from the existing, RBAC-gated `/security/questions`
 * route — no new gateway contract — and verify it against the agent's
 * Pass/Fail marks; no servicing data is read, so the screens are ID&V-flow
 * (verification-producing) rather than servicing screens.
 */

/**
 * Split a registration instruction line (e.g. "Select any 3 of 6. Check Hogan
 * for all answers.") into the leading count sentence and the remainder, for the
 * dark badge bar (#651): the count sentence renders bold-white, the rest grey.
 * The split is on the first sentence-terminating period; if there is no clear
 * split the whole line is treated as the (bold-white) count part.
 */
export function splitInstruction(text: string): {
  count: string;
  rest: string;
} {
  const match = text.match(/^(.*?[.!?])(\s+)(.+)$/s);
  if (match) {
    return { count: match[1] ?? text, rest: match[3] ?? '' };
  }
  return { count: text, rest: '' };
}

/** Card / info-box wrapper matching the PTSB design-system `InfoBox`. */
export function InfoBox({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={`bg-white border-2 border-[#dad7d3] p-6 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

export type CheckStatus = 'pass' | 'fail' | null;

/** Accessible Pass/Fail radio pair (faithful to the security-screen export). */
export function PassFailRadios({
  name,
  value,
  onChange,
  disabled = false,
  disablePass = disabled,
  disableFail = disabled,
}: {
  name: string;
  value: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
  disabled?: boolean;
  /**
   * Per-option disable overrides (default to {@link disabled}). Used by the
   * registration mandatory rows for @dylan219's #656 ruling that narrows the
   * fail-closed default (#348/#333/#626) for two specific not-on-file cases:
   * the landline confirmation with no landline on file allows BOTH marks, and
   * the date-of-birth row when not on file allows FAIL only (Pass stays
   * fail-closed). Every other row keeps the shared `disabled` for both.
   */
  disablePass?: boolean;
  disableFail?: boolean;
}): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-6"
      role="radiogroup"
      aria-label={`${name} verification result`}
    >
      {(['pass', 'fail'] as const).map((option) => {
        const id = `${name}-${option}`;
        const checked = value === option;
        const optionDisabled = option === 'pass' ? disablePass : disableFail;
        return (
          <div key={option} className="flex items-center gap-3">
            <input
              type="radio"
              id={id}
              name={name}
              checked={checked}
              disabled={optionDisabled}
              onChange={() => onChange(option)}
              className="sr-only peer"
            />
            <label
              htmlFor={id}
              className="relative flex items-center cursor-pointer group"
            >
              <div className="relative size-[24px] rounded-full border-2 border-[#979c9f] transition-all bg-white group-hover:border-[#6b7280] peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc]">
                {checked && (
                  <div className="absolute inset-0 m-auto size-[12px] rounded-full bg-[#1d252c]" />
                )}
              </div>
              <span className="ml-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none capitalize">
                {option}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  );
}

/** A labelled question row (label + optional hint + optional value + Pass/Fail radios). */
export function QuestionRow({
  label,
  value,
  listValue,
  hint,
  name,
  status,
  onChange,
  disabled = false,
  disablePass,
  disableFail,
}: {
  label: string;
  value?: string;
  /**
   * When the on-file answer has multiple entries (e.g. several joint accounts
   * or active Visa debit cards — #348), render each as its own list item
   * instead of one run-together string — mirrors the list-of-entries pattern
   * already used by `SecurityScreen.tsx`'s `IdentityQuestion` (issue #291) /
   * `EclHighSecurityScreen.tsx`. Takes precedence over `value` when non-empty.
   */
  listValue?: readonly string[];
  /**
   * Grey sub-instruction shown beneath the label (e.g. the "Check TSYS for
   * credit card limit" guidance on the VISA external checks — CCF-58). Purely
   * informational; rendered above any value.
   */
  hint?: string;
  name: string;
  status: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
  disabled?: boolean;
  /**
   * Per-option disable overrides forwarded to {@link PassFailRadios} (each
   * defaults to {@link disabled} when unset). See #656 — @dylan219's ruling
   * narrowing the fail-closed default for the not-on-file landline (both marks)
   * and date-of-birth (Fail only) rows.
   */
  disablePass?: boolean;
  disableFail?: boolean;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-4">
      <div className="flex flex-col">
        <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
          {label}
        </h3>
        {hint && (
          <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0 mb-1">
            {hint}
          </p>
        )}
        {listValue && listValue.length > 0 ? (
          <ul className="list-none m-0 p-0 space-y-1">
            {listValue.map((entry) => (
              <li
                key={entry}
                className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
              >
                {entry}
              </li>
            ))}
          </ul>
        ) : (
          value !== undefined && (
            <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
              {value || 'Not on file'}
            </p>
          )
        )}
      </div>
      <div className="ml-auto mt-5">
        <PassFailRadios
          name={name}
          value={status}
          onChange={onChange}
          disabled={disabled}
          disablePass={disablePass}
          disableFail={disableFail}
        />
      </div>
    </div>
  );
}

/** The customer's identity-question data from `/security/questions`. */
export interface SecurityQuestionsData {
  Dob?: string;
  MobileNumber?: string;
  MaskedMobile?: string;
  Landline?: string;
  MothersMaidenName?: string;
  /**
   * The customer's other products / accounts held with PTSB (Open24
   * registration Figma — shown when the agent ticks "Mother's maiden name not
   * on file" so they can ask "Do you have any other products or accounts with
   * PTSB?"). Sourced from the gateway; absent customers show "Not on file".
   */
  OtherProducts?: readonly string[];
  ActiveVisaDebitCards?: readonly string[];
  /**
   * Set by the BFF when the server-side `card/list` enrichment that populates
   * {@link ActiveVisaDebitCards} could not be verified (a transport/auth
   * failure or an in-band gateway decline — see `getSecurityQuestions` in
   * `banking/client.ts`, CCF-107/#345). An error is NOT equivalent to an
   * authoritative "no active Visa debit card"; the Open24 registration screen
   * (#407) and the ECL Medium/High screens fail closed on it — showing no
   * dropdown entries — so the agent can never mark the check off unverified
   * data.
   */
  ActiveVisaDebitCardsError?: boolean;
  /**
   * NOT sourced from this `/security/questions` payload for the Open24
   * confirmatory "Have you any joint accounts held in PTSB?" check — that
   * field is only overwritten by {@link RegistrationScreen}'s separate,
   * authoritative fetch of `/security/joint-associations` (issue #318,
   * mirroring the EDS High-Security fix, issue #282). Declared here only so
   * `AdditionalQuestion.answer` resolvers can read it off the merged object.
   */
  JointAccounts?: readonly string[];
  FirstSchool?: string;
  FavouriteMusic?: string;
  EyeColour?: string;
  StreetGrewUp?: string;
  FavouriteHoliday?: string;
  FavouriteSport?: string;
  /**
   * The Business24 "3 additional security questions" (first school attended /
   * favourite music / eye colour, CCF-57) as FREE TEXT — issue #305, Dylan's
   * refined requirement (2026-07-09) — AND Broker24's "4 additional security
   * questions" (street grew up on / favourite music / favourite holiday
   * destination / favourite sport, CCF-60) plus its "eye colour"/"first
   * school attended" mandatory items, which draw from the SAME source (issue
   * #311, mirroring issue #305's identical ruling for Business24): the real
   * gateway carries all of these on `customer/info`'s single
   * `SecurityQuestions` field with no formal schema or guaranteed order (an
   * agent free-types into it elsewhere), so the BFF comma-splits it and
   * returns the list as-is. Rendered verbatim, NEVER matched positionally to
   * `FirstSchool`/`FavouriteMusic`/`EyeColour`/`StreetGrewUp`/
   * `FavouriteHoliday`/`FavouriteSport` above (those remain the MOCK-ONLY
   * convenience fields with no real-gateway equivalent).
   */
  AdditionalQuestionAnswers?: readonly string[];
  VisaCreditLimit?: string;
  VisaCardholders?: readonly string[];
  PropertyAddress?: string;
  /**
   * Broker24's mandatory "signatory address" (CCF-60, issue #311) — sourced
   * from `customer/identification`'s address lines (`Address1..5`, joined),
   * NOT `PropertyAddress` (a Mortgage-only mock convenience field with no
   * real-gateway equivalent).
   */
  SignatoryAddress?: string;
  /**
   * Mortgage registration's mandatory "Borrower address" (CCF-59, issue
   * #319) — the identical gap/fix as `SignatoryAddress` above: sourced from
   * `customer/identification`'s address lines (`Address1..5`, joined), NOT
   * `PropertyAddress` (a Mortgage-only mock convenience field with no
   * real-gateway equivalent — the mock-only `customer/security-questions` op
   * 500s on the real gateway, issue #76).
   */
  BorrowerAddress?: string;
  MortgageBrokerOrBranch?: string;
  DirectDebitBank?: string;
  MortgageDrawdownYear?: string;
  MortgageTerm?: string;
  HomeInsuranceProvider?: string;
  LifeAssuranceProvider?: string;
}

interface OtpGenerateResult {
  sent: boolean;
  maskedMobile?: string;
  blocked?: boolean;
  message?: string;
  resultCode?: string;
}
interface OtpValidateResult {
  valid: boolean;
  resultCode?: string;
  message?: string;
  attemptsRemaining?: number;
  expired?: boolean;
  blocked?: boolean;
}

/** Formats an ISO `YYYY-MM-DD` date of birth as `DD/MM/YYYY`. */
export function formatDob(dob: string | undefined): string {
  if (!dob) return 'Not on file';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dob;
}

/**
 * Formats a mobile number for display (#712):
 *
 * - Irish numbers (`+353…` / `00353…` / `0…`) are rendered in national
 *   format without the country code (e.g. `085 8076526`), faithful to the
 *   CCF-8 / Open24 registration Figma.
 * - International numbers with a `00` international-access prefix (e.g.
 *   `0044…`) are normalised to `+<cc><subscriber>` form so the display is
 *   always consistent regardless of how the number was stored.
 * - Numbers already in `+E.164` form are returned as-is.
 * - Masked values (containing `*`) are returned unchanged to avoid
 *   fabricating digits.
 */
export function formatMobileNational(
  mobile: string | undefined,
): string | undefined {
  if (!mobile) return undefined;
  if (mobile.includes('*')) return mobile;
  const digits = mobile.replace(/[^\d+]/g, '');
  // Irish E.164 (+353…) or without the plus (353…).
  if (/^\+353\d+$/.test(digits)) {
    const national = '0' + digits.slice(4);
    return national.length > 3
      ? `${national.slice(0, 3)} ${national.slice(3)}`
      : national;
  }
  if (/^00353\d+$/.test(digits)) {
    const national = '0' + digits.slice(5);
    return national.length > 3
      ? `${national.slice(0, 3)} ${national.slice(3)}`
      : national;
  }
  // Irish national form: single leading 0 (not 00), e.g. 0871234123.
  if (/^0\d+$/.test(digits) && !digits.startsWith('00')) {
    return digits.length > 3
      ? `${digits.slice(0, 3)} ${digits.slice(3)}`
      : digits;
  }
  // International number stored with 00 prefix — normalise to + form.
  if (/^00\d+$/.test(digits)) {
    return '+' + digits.slice(2);
  }
  // Already in +E.164 form or bare digits — return as-is.
  if (digits.startsWith('+')) return digits;
  return mobile;
}

/**
 * Counts the DISTINCT, normalized (trimmed, case-insensitive), non-empty
 * answers in a free-text `AdditionalQuestionAnswers` list — Business24 and
 * Broker24 (ai-review MEDIUM, #305: "Duplicate answers satisfy two-answer
 * gate") independently hardened their `requiredAdditional` gate the same way.
 * The field is agent free-typed with no dedup guarantee, so a record like
 * `"Blue, Blue"` must NOT count as 2 distinct on-file answers — that would
 * let the agent mark `requiredAdditional` question rows Pass off a single
 * distinct fact. The raw list is still rendered to the agent verbatim
 * (duplicates and all) elsewhere — only this enablement count is
 * deduplicated.
 *
 * Previously NOT used for Broker24's mandatory eye-colour/first-school rows
 * (ai-review HIGH, #311): those originally failed closed unconditionally
 * regardless of distinct count, because the free-text field cannot identify
 * which fragment (if any) is specifically an eye colour or a school name.
 * Issue #349 (Dylan, revising #311) instead makes those two rows agent-judged
 * `externalCheck`-style questions — the agent asks the customer and checks
 * the answer against the same shared free-text list shown in "additional
 * questions" below, exactly like the VISA/Mortgage TSYS/Hogan checks — so
 * this counter no longer gates them; see `Broker24RegistrationScreen`.
 *
 * Exported for reuse by `SecurityScreen.tsx`'s BUS24 Medium/High
 * `knowledgeGroup` (SCRN-05/10, CCF-23/42, issue #334) — the identical
 * unordered-free-text gating problem, applied to a "confirm any 1 of N" radio
 * group instead of independent Pass/Fail rows.
 */
export function countDistinctAnswers(
  answers: readonly string[] | undefined,
): number {
  if (!answers?.length) return 0;
  const normalized = answers
    .map((a) => a.trim().toLowerCase())
    .filter((a) => a.length > 0);
  return new Set(normalized).size;
}

/** A single mandatory identity item shown at the top of a registration screen. */
export interface MandatoryItem {
  /** Stable key (used for the radio name). */
  readonly key: string;
  /** Display label. */
  readonly label: string;
  /** Resolves the value from the loaded security-questions data. */
  readonly value: (
    q: SecurityQuestionsData,
    name?: string,
  ) => string | undefined;
  /**
   * Grey sub-instruction shown beneath the label (e.g. "Check TSYS for credit
   * card limit"). Informational only.
   */
  readonly hint?: string;
  /**
   * When true this item is verified by the agent against an EXTERNAL system
   * (TSYS, for the VISA credit-limit / cardholder checks — CCF-58). It has NO
   * on-file value in our data: the screen shows only the question + hint and the
   * Pass/Fail radios, never a value, and the agent marks Pass/Fail on their own
   * TSYS check. Such an item is therefore exempt from the on-file-value
   * fail-closed rule the identity items obey, but still requires an explicit
   * Pass to contribute to a passing outcome.
   */
  readonly externalCheck?: boolean;
}

/** A selectable additional security question. */
export interface AdditionalQuestion {
  readonly key: string;
  readonly label: string;
  /** Resolves the on-file answer for display (optional — agent reads it out). */
  readonly answer?: (q: SecurityQuestionsData) => string | undefined;
  /**
   * Resolves the on-file answer as a list of DISCRETE entries (e.g. several
   * joint accounts, or several active Visa debit cards — issue #348) instead
   * of one run-together string — mirrors `SecurityQuestion.resolveList`
   * (issue #291). Takes precedence over {@link answer} when it returns a
   * non-empty array; an empty/absent result falls back to "Answer not on
   * file" exactly like an absent `answer`.
   */
  readonly answerList?: (
    q: SecurityQuestionsData,
  ) => readonly string[] | undefined;
  /**
   * Label shown when {@link answerList} resolves to a GENUINELY EMPTY (but
   * defined) list — i.e. the authoritative source loaded and returned no
   * entries — as opposed to being absent/unavailable/not-yet-loaded (which
   * keeps the default "Answer not on file"). Issue #476: for the Open24
   * joint-accounts confirmation, a customer with no joint accounts must read
   * "No joint accounts" rather than the ambiguous "Answer not on file"
   * (`/security/joint-associations` returns an empty `[]` when the customer
   * has none — see `RegistrationScreen`'s fetch — vs `undefined` when the
   * fetch failed or has not yet resolved). Only meaningful with
   * {@link answerList}.
   */
  readonly answerListEmptyLabel?: string;
  /**
   * Render the on-file answer entries (from {@link answerList}) as a SELECT
   * dropdown for the agent to verify against, instead of a bullet list —
   * faithful to the Open24 registration Figma (`update-reg-eds`), which shows
   * the "Do you have an active Visa debit card?" check as a dropdown of the
   * customer's active Visa-debit card numbers (full number), placeholder
   * "Active Visa cards numbers" (issue #367). Requires {@link answerList}. An
   * empty/absent list renders the (disabled) placeholder-only dropdown rather
   * than the "Answer not on file" bullet, so the affordance the agent expects
   * is always present. Other additional questions (e.g. joint accounts, which
   * the Figma shows as a text list) leave this unset.
   */
  readonly answerDropdown?: boolean;
  /**
   * Placeholder shown as the first, non-selectable option of an
   * {@link answerDropdown} (e.g. "Active Visa cards numbers"). Defaults to
   * "Select…" when unset.
   */
  readonly answerDropdownPlaceholder?: string;
  /**
   * Grey sub-instruction shown beneath the label (e.g. "Check Hogan for this
   * answer"). Informational only.
   */
  readonly hint?: string;
  /**
   * When true this question is verified by the agent against an EXTERNAL
   * system with NO API integration (Hogan, for the mortgage registration
   * questions — CCF-59, issue #319, mirroring the identical `MandatoryItem`
   * flag above and the Mortgage Security screen's fix, issue #309). The
   * screen shows only the question + `hint` and the Pass/Fail radios, never
   * an on-file value/"Answer not on file" fallback — the agent verifies the
   * answer with Hogan directly and records the outcome. Such a question is
   * exempt from the "answer on file" fail-closed rule the other additional
   * questions obey, but still requires an explicit Pass to count toward the
   * "N of M" requirement.
   */
  readonly externalCheck?: boolean;
}

/** Declarative configuration for a registration screen. */
export interface RegistrationConfig {
  /** RBAC screen code (SCRN-17..21). */
  readonly screenCode: string;
  /** Page heading (faithful to the Figma). */
  readonly heading: string;
  /** Mandatory identity items (all must be marked Pass to succeed). */
  readonly mandatory: readonly MandatoryItem[];
  /** Additional security questions (the agent selects + marks `requiredAdditional`). */
  readonly additional: readonly AdditionalQuestion[];
  /** How many of the additional questions the agent must mark (e.g. 2 of 3). */
  readonly requiredAdditional: number;
  /** Whether the 2FA OTP step applies (CCF-16). */
  readonly twoFactor: boolean;
  /** Extra guidance shown beneath the transfer actions (e.g. Broker IMC note). */
  readonly transferNote?: string;
  /**
   * Open24-only extra sections, faithful to the `update-reg-eds` Figma:
   * a "Mother's maiden name not on file" checkbox that reveals the customer's
   * other products/accounts; a "Mobile number not on file" checkbox that
   * reveals the landline on file; a Visa-debit-card + joint-accounts box; and
   * the CCF-16 read-back scripting on the 2FA step. Other registration
   * variants leave this unset and keep the plain mandatory/additional flow.
   * Implies {@link notOnFileToggles} (see below) — Open24 does not need to set
   * both.
   */
  readonly open24Extras?: boolean;
  /**
   * Business24 (SCRN-18 / CCF-57), issue #333: enables JUST the "not on file"
   * checkbox pair on the maiden-name / mobile-number mandatory rows — the
   * "Mother's maiden name not on file" tick-box revealing "Do you have any
   * other products or accounts with PTSB?" and the "Mobile number not on
   * file" tick-box revealing the landline — WITHOUT pulling in the rest of
   * `open24Extras`' bundle (the joint-accounts
   * confirmatory fetch, the CCF-16 2FA read-back script, or the "confirmatory
   * checks are always markable" rule), none of which CCF-57 asks for. Open24
   * gets this behaviour for free via `open24Extras` above; other variants
   * leave both unset.
   */
  readonly notOnFileToggles?: boolean;
  /**
   * Business24 (SCRN-18 / CCF-57) and Broker24 (SCRN-21 / CCF-60) only —
   * issue #311, mirroring issue #305's ruling for Business24: the additional
   * questions' on-file answers are free text from `customer/info` with NO
   * guaranteed order or 1:1 mapping to the script questions below — an agent
   * free-types into the source field elsewhere. When set, the per-question
   * `answer` resolvers are ignored for display; instead the shared
   * `AdditionalQuestionAnswers` list is rendered once, verbatim, and every
   * row is enabled/disabled together (any answer on file lets the agent mark
   * any of the questions, since which value belongs to which question is not
   * knowable). Other registration variants leave this unset.
   */
  readonly additionalAnswersUnordered?: boolean;
  /**
   * Overrides the default instructional copy shown above the additional-
   * questions box (e.g. Mortgage/CCF-59's "validates against Hogan" — issue
   * #319, since the default "Only questions with an answer on file can be
   * marked" text does not apply to `externalCheck` questions, which have no
   * on-file value by design). Other registration variants leave this unset
   * and keep the default copy.
   */
  readonly additionalInstructions?: string;
  /**
   * Broker24 (SCRN-21 / CCF-60) only — issue #349, Dylan's follow-up on #311:
   * renders a tick-box in front of EACH additional question instead of every
   * row being independently markable at once. The agent ticks the ONE
   * question they are actually asking the customer — ticking a different box
   * deselects whichever was previously selected, so at most one is ever
   * selected (CCF-60: "only one additional question needs to be answered") —
   * and only that selected row's Pass/Fail control is shown; the other rows
   * show just their tick-box + question text, with no Pass/Fail control.
   * Other registration variants leave this unset and keep every additional
   * row independently markable (Business24/Mortgage's own tickets).
   */
  readonly additionalSelectable?: boolean;
  /**
   * Mortgage (SCRN-20 / CCF-59) only — issue #350, Dylan's follow-up: like
   * {@link additionalSelectable} but the agent must select EXACTLY this many
   * of the additional questions (Mortgage: any 3 of 6). A tick-box renders in
   * front of every question; only a ticked question's Pass/Fail control is
   * shown, the agent cannot tick more than this many (further tick-boxes are
   * disabled once the limit is reached), and the overall verification passes
   * only when all of the selected questions are marked Pass. Distinct from
   * {@link additionalSelectable} (Broker24's "select exactly ONE"), which
   * clears any prior selection on each tick; here up to the limit are held
   * simultaneously. Set together with `requiredAdditional` (= the same
   * number). Other registration variants leave this unset.
   */
  readonly additionalSelectableLimit?: number;
  /**
   * Business24 (SCRN-18 / CCF-57) only — issue #648, @dylan219's Option A
   * ruling (2026-08-17): once the agent has TICKED a selectable additional
   * question, its Pass/Fail control is ALWAYS markable — the agent asks the
   * customer and verbally verifies against the unordered "answers on file"
   * free-text list shown on the page — instead of the control being disabled
   * unless `countDistinctAnswers >= requiredAdditional`. This SUPERSEDES the
   * #305 enable gate for Business24: the real staging `customer/info` free-text
   * field is usually empty, so that gate left every Pass/Fail control disabled
   * (the reported symptom in #648). The "no Pass off a single/duplicate on-file
   * fact" concern #305 raised is instead satisfied by the explicit
   * select-EXACTLY-`additionalSelectableLimit` requirement + verbal
   * verification — the same model already approved for the Mortgage Hogan
   * questions (#350, `externalCheck`) and the VISA/TSYS external checks. Only
   * set on the `additionalAnswersUnordered` + `additionalSelectableLimit`
   * Business24 screen; Broker24 (#311/#349) deliberately KEEPS the
   * distinct-answer gate (leave this unset there).
   */
  readonly additionalAlwaysMarkable?: boolean;
  /**
   * Issue #653 (@dylan219), extended to ALL registration screens by #729
   * (Open24 / Business24 / VISA / Mortgage / Broker24, SCRN-17..21 — @dylan219
   * 2026-08-31: "in the same way and using the same rules you have on the
   * business and broker screens"): once ALL registration security questions
   * (+ 2FA where the screen has one) pass (`securityPass`), offer a "Reset
   * internet password" option that navigates to the Reset Internet Password
   * page (SCRN-57) to reset the customer's IPW. Completion of the registration
   * questions is treated as sufficient security (no extra 2FA gate for this
   * navigation) — mirroring the existing broker security → IPW option (#568).
   * The reset page, when reached this way, shows a Back button that returns to
   * THIS registration screen with every answer + the Pass (the server-side
   * `medium` verification) intact and offers NO navigation to any other screen.
   * NOTE (PTSB): the underlying gateway reset flow is PTSB-side pending; the
   * option is wired in parallel with their gateway fix and surfaces whatever
   * the gateway returns (#729).
   */
  readonly ipwReset?: boolean;
  /**
   * Mortgage (SCRN-20 / CCF-59) ONLY — issue #774 (@dylan219, 2026-09-16):
   * renders an "Unable to complete 2FA" tick box BESIDE the Send Code button
   * on the 2FA step. Ticking it records the 2FA piece a FAIL SERVER-SIDE
   * against the session-bound Registration Security run (the #534 run seam —
   * never a client-declared flag, the #729 lesson), but the fail outcome /
   * messaging (FAIL badge + branch-referral banner) is DEFERRED until EVERY
   * other question is completed — reusing the #781 `allQuestionsAnswered`
   * deferral the Broker24 journey established. The routes the fail records
   * through are gated by the dedicated `recordRegistration2faFail` permission
   * (#774 reopen: every role with SCRN-20 screen access EXCEPT ECLAgent — the
   * tick box is for the agents who run mortgage registration), so the box
   * renders for every non-ECL mortgage role and stays hidden for ECL, which
   * keeps only the #534 skip dropdown ("On ECL, please leave this exactly as
   * it is"). It also stays hidden fail-closed until the server session's role
   * resolves (the !1134 ai-review fail-closed convention). No other
   * registration screen sets this: the ECL skip dropdown stays exactly as it
   * is.
   */
  readonly unableToComplete2faTickBox?: boolean;
  /**
   * Mortgage (SCRN-20 / CCF-59) only — issue #680, @philipc39: suppress the
   * "Please leave a note that the customer has been referred to branch."
   * sentence from the failed-registration branch-referral banner. The primary
   * "does not match our records / visit a branch" guidance still shows; only
   * the note-taking instruction is removed on this screen. Other registration
   * variants leave this unset and keep the note line.
   */
  readonly hideReferralNote?: boolean;
  /**
   * Broker24 (SCRN-21 / CCF-60) — issue #719, CCRS-3498: replaces the default
   * "does not match our records / visit a branch" failure message with a
   * Broker24-specific line. When absent the shared default is shown.
   */
  readonly failureText?: string;
  /**
   * Broker24 (SCRN-21 / CCF-60) — issue #719, CCRS-3498: replaces the default
   * "Please leave a note that the customer has been referred to branch" line in
   * the failure banner. Ignored when `hideReferralNote` is set. When absent the
   * default note is shown.
   */
  readonly failureNote?: string;
  /**
   * Broker24 (SCRN-21 / CCF-60) — issue #772, CCF-318 Unhappy Path #2: when the
   * broker has NO security questions on file (the free-text
   * `AdditionalQuestionAnswers` list is empty) there is nothing to verify the
   * additional questions — or the eye-colour / first-school external-check rows,
   * which are judged against that SAME shared list (#349) — against, so security
   * cannot be completed. When set, an "insufficient information on file" note is
   * shown and a passing outcome is blocked. Scoped to Broker24 only: Business24
   * (SCRN-18) deliberately stays always-markable with an empty free-text field
   * (`additionalAlwaysMarkable`, #648), so it leaves this unset. Other variants
   * leave it unset too.
   */
  readonly insufficientDataWhenNoAnswers?: boolean;
  /**
   * Open24 (SCRN-17) / Business24 (SCRN-18) / VISA (SCRN-19), #774 (@dylan219,
   * 2026-09-11): the mobile-number identity question is ALWAYS markable Pass/Fail
   * — even with no mobile on file — and offers NO alternate question (this
   * supersedes the mobile "not on file" → landline-alternate behaviour of
   * #348/#656/#662/#372). A FAIL on the mobile question auto-fails 2FA, with the
   * fail outcome deferred until all other questions are completed.
   */
  readonly mobileQuestionAlwaysMarkable?: boolean;
}

/**
 * The five registration pages, as a switcher (#163, @dylan219). Each entry
 * carries the route and a human label; `screenCode` identifies which page a
 * given {@link RegistrationConfig} is, so the switcher can show the current page
 * as selected. Order matches the agent-facing menu (Open24 first — the default
 * landing page for an unregistered card). Keyed by `screenCode` so the shared
 * component does not need a new config field threaded through all five screens.
 */
const REGISTRATION_PAGES: ReadonlyArray<{
  readonly screenCode: string;
  readonly route: string;
  readonly label: string;
}> = [
  { screenCode: 'SCRN-17', route: '/registration/open24', label: 'Open24' },
  {
    screenCode: 'SCRN-18',
    route: '/registration/business24',
    label: 'Business24',
  },
  { screenCode: 'SCRN-19', route: '/registration/visa', label: 'VISA' },
  {
    screenCode: 'SCRN-20',
    route: '/registration/mortgage',
    label: 'Mortgage',
  },
  { screenCode: 'SCRN-21', route: '/registration/broker24', label: 'Broker24' },
];

/**
 * The Customer Notes screen (SCRN-14) route, offered as an extra option in the
 * registration switcher (#654, CCRS-3433). When a customer FAILS registration
 * security the agent must leave a note on the account recording that
 * registration failed; Customer Notes is deliberately reachable and usable
 * PRE-medium verification (#237, @dylan219), so a failed registration (which
 * never elevates the customer) can still record one. This is a plain,
 * RBAC-gated navigation to the existing Customer Notes screen — it reuses the
 * built route and adds no gateway contract and no verification bypass.
 */
const CUSTOMER_NOTES_ROUTE = '/customer-notes';

/**
 * The "process menu" registration-page switcher (#163, @dylan219). An agent who
 * lands on the default Open24 page (because an unregistered card has no
 * resolvable customer segment) can switch to any of the five registration pages.
 * The current page (resolved from the config's `screenCode`) is shown selected.
 *
 * It also offers a "Customer Notes" option (#654, CCRS-3433) that navigates to
 * the Customer Notes screen (SCRN-14) so the agent can record a
 * registration-security failure as a note on the account. Customer Notes is not
 * a registration "page", so selecting it navigates away without ever becoming
 * the switcher's selected value (the switcher stays on the current registration
 * page).
 */
function RegistrationSwitcher({
  screenCode,
}: {
  screenCode: string;
}): React.JSX.Element {
  const navigate = useNavigate();
  const current =
    REGISTRATION_PAGES.find((p) => p.screenCode === screenCode)?.route ?? '';
  return (
    <div className="flex items-center gap-3">
      <label
        htmlFor="registration-switcher"
        className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        Change registration type
      </label>
      <select
        id="registration-switcher"
        aria-label="Change registration type"
        value={current}
        onChange={(e) => {
          const route = e.target.value;
          if (route && route !== current) navigate(route);
        }}
        className="px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white border-[#dad7d3] hover:border-[#979c9f] focus:outline-none focus:ring-4 focus:border-[#005fcc] focus:ring-[#005fcc]/20 min-w-[200px]"
      >
        {REGISTRATION_PAGES.map((p) => (
          <option key={p.screenCode} value={p.route}>
            {p.label}
          </option>
        ))}
        <option value={CUSTOMER_NOTES_ROUTE}>Customer Notes</option>
      </select>
    </div>
  );
}

/**
 * Mandatory pre-transfer scripting (CCF-59/60), read verbatim by the agent.
 *
 * #637 (SCRN-17 / CCF-59) — this regulatory fraud-warning script is
 * admin-editable content-catalogue text (`registration.mandatory_script`,
 * `regulatory: true`). This compiled-in constant is the first-paint fallback
 * default used until the `/content` override resolves (or if it is unavailable);
 * it is rendered as PLAIN TEXT only. No registration logic reads this string.
 */
const MANDATORY_SCRIPT =
  'I’ll transfer you now to our automated Self Service to choose your ' +
  'personal access number and get your temporary internet password. PTSB ' +
  'will never ask you to confirm the full six digits of your Personal Access ' +
  'Number; we will only ever ask you for three random digits from this ' +
  'number. PTSB will also never contact you by email or text message asking ' +
  'you to confirm or update any personal details.';

/**
 * The static "related products/accounts" list shown under "Do you have any
 * other products or accounts with PTSB?" when the agent ticks "Mother's maiden
 * name not on file". Issue #361 (CCF-8/CCF-46/CCF-57) established that this
 * bullet list is ALWAYS shown as the fallback prompt — mirroring the identical
 * fallback on the other security screens (`EclHighSecurityScreen.tsx`,
 * CCF-107) — regardless of whether the gateway returned a specific
 * `OtherProducts` array (which is empty against the real gateway).
 *
 * The list therefore doubles as the maiden row's verification alternative: when
 * the toggle is ticked the agent verifies the customer against THIS displayed
 * list, so the row is always markable Pass/Fail (issue #372 — the row was
 * disabled whenever the gateway sent no `OtherProducts`, contradicting CCF-57
 * "Each section requires Pass or Fail"). Rendered from this single constant so
 * the displayed prompt and the Pass/Fail gate can never drift apart.
 */
export const MAIDEN_FALLBACK_PRODUCTS: readonly string[] = [
  'Savings Account',
  'Mortgage',
  'Credit Card',
  'Loan',
];

/**
 * One of the customer's ACTUAL other products/accounts, as returned by
 * `/security/products-summary` (the real `customer/accounts/list` +
 * `customer/products/list` gateway ops — the same sources the Dashboard already
 * uses). Issues #389/#604.
 */
interface ProductSummaryRow {
  // Always present — a row (account or product) is only emitted when its
  // masked id carries at least 4 trailing digits (issue #604).
  AccountLast4: string;
  AccountType?: string;
  Nickname?: string;
  // Present only for a non-account product row (Visa/mortgage/insurance/
  // investment — issue #604).
  ProductDescription?: string;
}

/**
 * Formats one of the customer's ACTUAL other products/accounts for the
 * maiden-name-not-on-file confirmation list (issue #389) — the nickname when
 * set, otherwise the account type, plus the last 4 digits (never the full
 * number), e.g. "Switch Current A/C ••4701". Mirrors `SecurityScreen.tsx`'s
 * `formatProductEntry` exactly, so the registration and security screens render
 * the same customer accounts identically.
 */
function formatProductEntry(p: ProductSummaryRow): string {
  // A non-account product row (Visa credit card, mortgage, insurance,
  // investment — issue #604) carries `ProductDescription`; an account row uses
  // the nickname/type. Every row has a last-4 suffix (rows without trailing
  // digits are dropped server-side), so it is always appended.
  const desc =
    p.Nickname?.trim() ||
    p.ProductDescription?.trim() ||
    p.AccountType?.trim() ||
    'Account';
  return `${desc} ••${p.AccountLast4}`;
}

/**
 * The shared Registration screen. Renders the mandatory identity tier, the
 * additional-question tier (any N of M), the optional 2FA OTP step, the
 * mandatory scripting + "Scripting complete" gate, and the transfer / manual-
 * password actions. Security passes only when every mandatory item is Pass, the
 * required number of additional questions are answered with none failed, and
 * (when applicable) 2FA passes.
 */
export function RegistrationScreen({
  config,
}: {
  config: RegistrationConfig;
}): React.JSX.Element {
  const navigate = useNavigate();
  const { customer, updateCustomer } = useCustomer();
  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';

  // @dylan219 #784 — the CCF-43 auth-hold + J&N fraud/AML check is ONLINE-ONLY.
  // The server-pinned Online/Offline journey (`data.state` from `GET /session`)
  // is resolved by the session effect below and passed to `useAuthHoldCheck`.
  // `null` until it resolves (the hook stays fail-CLOSED); `true` = online (run
  // the check); `false` = offline (skip it — no J&N block).
  const [isOnline, setIsOnline] = useState<boolean | null>(null);

  // CCF-43 (issue #666) — run the SAME Customer Authentication Hold Check when
  // the agent lands on this Registration security screen. CCF-43 states "Below
  // should apply to all hold checks" — a customer who is registering must have
  // the auth hold check completed + the pop-up raised, exactly as it is on the
  // Dashboard (Online-login landing, issue #67). Reuses the single
  // {@link useAuthHoldCheck} hook (same `/idv/auth-hold-check` gateway call,
  // same fail-open contract, same `AuthHoldPopup`) so behaviour is identical.
  // Enabled here (a registering customer is the online-card journey), but the
  // check is now ONLINE-ONLY per @dylan219 #784: it only runs on a live Genesys
  // interaction. On an OFFLINE registration it is SKIPPED entirely, and an
  // offline-skipped J&N check does NOT block progression (`online={isOnline}`
  // below resolves the J&N tri-state to complete-with-no-block offline). The
  // pop-up auto-raise is suppressed once acknowledged for this customer on this
  // call (issue #574), the ack persisted to the shared context so it does not
  // re-pop as the agent moves between the security screens.
  // The SAME hook ALSO surfaces the #669 J & N Hold Handling verdict (issue
  // #666, @dylan219 2026-08-19: "Yes it should be wired to the registration
  // screens too along with the previous ones you added"). A registering
  // customer whose accounts carry BOTH a `J` ("Multiple Warnings") and an `N`
  // ("Pay No Withdrawal") hold simultaneously is a fraud/AML stop condition —
  // the agent must transfer to the fraud team. The verdict rides on the SAME
  // `/idv/auth-hold-check` response (the ID&V route already loads the per-account
  // holds at the identified stage, before medium verification, so no separate
  // medium-gated accounts read is needed) and that route ALSO records the
  // server-side `requireNoJnHoldBlock` stop, which is the authoritative control.
  // The J&N warning is distinct from the CCF-43 auth-hold pop-up above (J/N are
  // not auth-hold codes) and can raise even when there is no auth hold. Since
  // 2026-09-01 (@dylan219) it is NOT a per-screen dismissible pop-up: the hook
  // publishes a found verdict to the app-shell navigation-lock store and App.tsx
  // renders the NON-DISMISSIBLE warning in place of the routed screens on every
  // route — no acknowledge, no navigation off it. (The fail-closed handling of
  // `jnHoldError` / an in-flight check below is a DIFFERENT state with no
  // warning raised, so the inline error remains this screen's surface.)
  const {
    authHold,
    authHoldOpen,
    openAuthHold,
    closeAuthHold,
    jnHold,
    jnHoldError,
    jnHoldCheckComplete,
  } = useAuthHoldCheck({
    customerNumber,
    tellerId,
    // @dylan219 #784 — ONLINE-ONLY. `null` until `GET /session` resolves (the
    // hook stays fail-CLOSED — jnHoldCheckComplete false — so progression is
    // blocked until the state is known); `false` (offline) skips the check and
    // resolves the J&N verdict to no-block so offline registration can progress.
    online: isOnline,
    acknowledged: customer.authHoldAcknowledged,
    onAcknowledge: () => updateCustomer({ authHoldAcknowledged: true }),
  });

  // #653 — when the agent returns from the Reset Internet Password page via its
  // Back button, restore this screen's answer snapshot. PEEK it ONCE at first
  // render (a pure read — no storage mutation in render, so a React StrictMode
  // double-render is safe) into a ref, and CONSUME the key later in an effect
  // (below). The snapshot seeds the answer state's lazy initialisers below and —
  // crucially — survives the data-load effect's cross-customer reset (that
  // effect skips its reset once on a restore mount, see `skipResetOnceRef`). A
  // normal (non-round-trip) visit or a fresh customer finds no snapshot and
  // starts clean.
  const restoredSnapshotRef = useRef<
    ReturnType<typeof peekRegistrationAnswers> | 'none'
  >(undefined);
  if (restoredSnapshotRef.current === undefined) {
    restoredSnapshotRef.current =
      config.ipwReset && customerNumber
        ? (peekRegistrationAnswers(config.screenCode, customerNumber) ?? 'none')
        : 'none';
  }
  const restoredSnapshot =
    restoredSnapshotRef.current === 'none'
      ? undefined
      : restoredSnapshotRef.current;
  // Consume the peeked snapshot key on mount (never during render) so it only
  // ever restores ONCE — StrictMode-safe. Bound to the screen + customer it was
  // read for.
  useEffect(() => {
    if (restoredSnapshot && config.ipwReset && customerNumber) {
      clearRegistrationAnswers(config.screenCode, customerNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // One-shot flag: the data-load effect skips its cross-customer answer clears on
  // the FIRST run of a restore mount (so the restored snapshot survives), then
  // reverts to clearing as normal. Initialised from whether a snapshot was
  // restored at first render.
  const skipResetOnceRef = useRef(restoredSnapshot !== undefined);

  // #637 (SCRN-17 / CCF-59) — the mandatory pre-transfer script is regulatory
  // content-catalogue text; it falls back to the compiled-in `MANDATORY_SCRIPT`
  // default so the screen renders unchanged when there is no override. Rendered
  // as PLAIN TEXT only.
  const mandatoryScript = useContent('registration.mandatory_script');

  const [questions, setQuestions] = useState<SecurityQuestionsData | null>(
    null,
  );
  const [loadError, setLoadError] = useState(false);

  // Open24 (issue #318): the "Have you any joint accounts held in PTSB?"
  // confirmatory check's on-file value, sourced from the REAL gateway
  // `customer/joint-associations` op (via `/security/joint-associations`) —
  // NEVER from `questions.JointAccounts`, which is carried by the mock-only
  // `customer/security-questions` op the mandatory items above read (the real
  // PTSB gateway 500s for that op and never returns a JointAccounts field on
  // it — issue #76). This mirrors the EDS High-Security joint-accounts fix
  // (issue #282) exactly. Kept as separate state (rather than merged into
  // `questions`) so a slower/faster resolution race between this fetch and the
  // `/security/questions` load can never drop either value.
  const [jointAccounts, setJointAccounts] = useState<
    readonly string[] | undefined
  >(undefined);

  // Issue #389: the customer's ACTUAL other products/accounts, sourced from the
  // REAL gateway `customer/accounts/list` op via `/security/products-summary` —
  // exactly as `SecurityScreen.tsx` does for its "Mother's maiden name not on
  // file" branch. `undefined` = not yet resolved (or none on file); a resolved
  // non-empty array is rendered as masked entries (e.g. "Switch Current A/C
  // ••4701") in the products prompt, in preference to the static
  // `MAIDEN_FALLBACK_PRODUCTS` list. Best-effort: a failure or an empty result
  // leaves it undefined, so the prompt falls back to the static list (the maiden
  // row stays markable Pass/Fail — #361/#372, CCF-57).
  const [otherProducts, setOtherProducts] = useState<
    readonly ProductSummaryRow[] | undefined
  >(undefined);

  const [mandatoryStatus, setMandatoryStatus] = useState<
    Record<string, CheckStatus>
  >(() => restoredSnapshot?.mandatoryStatus ?? {});
  const [additionalStatus, setAdditionalStatus] = useState<
    Record<string, CheckStatus>
  >(() => restoredSnapshot?.additionalStatus ?? {});

  // Open24 (#367): the agent's currently-selected entry in an
  // `answerDropdown` question's SELECT (e.g. the active Visa-debit card they
  // are verifying against), keyed by question key. Purely a UI reference aid —
  // it does not gate the Pass/Fail outcome (the confirmatory checks are
  // none-mandatory, #42) — but it must clear across a customer/Teller-ID change
  // so a prior customer's selection can never leak (mirrors the cards/joint
  // reset pattern).
  const [additionalDropdownChoice, setAdditionalDropdownChoice] = useState<
    Record<string, string>
  >(() => restoredSnapshot?.additionalDropdownChoice ?? {});

  // Broker24 (issue #349, revising #311): which single additional question
  // the agent has ticked to ask, when `config.additionalSelectable` is set.
  // Ticking a different question clears any previous Pass/Fail mark — the
  // agent is no longer being asked to judge the deselected question, so its
  // stale mark must never silently keep counting toward the "1 of N" gate.
  const [selectedAdditionalKey, setSelectedAdditionalKey] = useState<
    string | null
  >(() => restoredSnapshot?.selectedAdditionalKey ?? null);
  const selectAdditional = (key: string, checked: boolean): void => {
    setSelectedAdditionalKey((prev) => {
      if (checked) return key;
      return prev === key ? null : prev;
    });
    setAdditionalStatus({});
  };

  // Mortgage (issue #350, Dylan's follow-up): when `additionalSelectableLimit`
  // is set the agent selects EXACTLY that many additional questions (any 3 of
  // 6). Held as a set of keys; ticking a further question once the limit is
  // reached is prevented (the remaining tick-boxes are disabled), and
  // un-ticking a question clears any Pass/Fail mark it had so a stale mark can
  // never keep counting toward the outcome once the agent has deselected it.
  const [selectedAdditionalKeys, setSelectedAdditionalKeys] = useState<
    readonly string[]
  >(() => restoredSnapshot?.selectedAdditionalKeys ?? []);
  const selectLimit = config.additionalSelectableLimit;
  const toggleAdditional = (key: string, checked: boolean): void => {
    setSelectedAdditionalKeys((prev) => {
      if (checked) {
        if (prev.includes(key)) return prev;
        // Guard against exceeding the limit even if a disabled control is
        // somehow toggled (defence in depth alongside the disabled tick-box).
        if (selectLimit != null && prev.length >= selectLimit) return prev;
        return [...prev, key];
      }
      return prev.filter((k) => k !== key);
    });
    if (!checked) {
      // Clear the deselected question's mark; a stale Pass must not linger.
      setAdditionalStatus((s) => {
        const { [key]: _drop, ...rest } = s;
        return rest;
      });
    }
  };

  // 2FA OTP state (CCF-16) — reuses the Medium-Security pattern.
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [generating, setGenerating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [otpStatus, setOtpStatus] = useState<'pass' | 'fail' | null>(
    () => restoredSnapshot?.otpStatus ?? null,
  );
  // Non-blocking inline warning for a wrong (non-final) OTP code (issue #375,
  // per @dylan219's inline-error UX rule #80/#82/#94 — mirrors
  // `SecurityScreen.tsx`'s `otpError`): a wrong OTP must surface the gateway
  // code + message INLINE beside the Validate button and let the agent retry,
  // never a silent no-op / toast-only. Cleared on a fresh validate attempt and
  // on the pass / expired / blocked branches (which render their own copy).
  const [otpError, setOtpError] = useState<{
    code?: string;
    message: string;
    // Which action produced the error, so it renders directly under THAT
    // button (#782): 'generate' → under Send Code; 'validate' → under Validate.
    source: 'generate' | 'validate';
  } | null>(null);
  const [otpAttemptsRemaining, setOtpAttemptsRemaining] = useState<
    number | null
  >(null);

  // Issue #534 — the ECL-only "reason for non-completion" (skip-2FA) dropdown
  // on the 2FA step. The SAME field as the Security screens' skip-2FA (#287):
  // the tester ruled that on the registration screens it is offered to ECL
  // agents ONLY. The agent's role comes from the server session (`/session`);
  // until it resolves the dropdown stays hidden (safe default — hidden is
  // correct for every non-ECL role). The server-side trio
  // (`/security/registration/{begin,skip-2fa,complete}`) is gated by the
  // dedicated, ECLAgent-only `skipRegistration2fa` permission, so a non-ECL
  // agent cannot drive the skip even by crafting a request (fail-closed).
  const [agentRole, setAgentRole] = useState<string | null>(null);
  // ai-review HIGH (#774) — the mobile always-markable gate below is a
  // security-sensitive ID&V/registration flow, so it must FAIL CLOSED while the
  // role is still resolving. `roleLoaded` flips true ONLY once `/session` has
  // actually returned (mirrors the `isOnline` fail-closed-until-resolved
  // convention in this same effect); on an error it stays false, keeping the
  // restrictive ECL path. Without this the flag would default to the less
  // restrictive non-ECL behaviour during the load window, briefly letting an ECL
  // agent bypass the mobile-not-on-file → alternate-question requirement.
  const [roleLoaded, setRoleLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.GET('/session');
        if (!cancelled) {
          setAgentRole((data as { role?: string } | undefined)?.role ?? null);
          setRoleLoaded(true);
          // @dylan219 #784 — the CCF-43 auth-hold + J&N check is ONLINE-ONLY.
          // Capture the server-pinned journey state so it can gate the hook
          // below. `null` until resolved (fail-CLOSED: the hook does not run and
          // does not mark the J&N check complete until online/offline is known).
          const state = (data as { state?: string } | undefined)?.state;
          if (state === 'online' || state === 'offline') {
            setIsOnline(state === 'online');
          }
        }
      } catch {
        // Non-fatal: the dropdown simply never renders (fail-hidden) and
        // `isOnline` stays null (the auth-hold check stays fail-closed).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const canSkip2fa = config.twoFactor && agentRole === 'ECLAgent';
  // #774 (@dylan219 reopen) — the Mortgage "Unable to complete 2FA" tick box
  // is for the agents who run mortgage registration, NOT ECL-scoped: an ECL
  // agent keeps ONLY the #534 skip dropdown above ("On ECL, please leave this
  // exactly as it is") and never sees the tick box; every OTHER role that can
  // reach the Mortgage registration screen does. Like the ECL dropdown and the
  // !1134 `mobileAlwaysMarkable` convention this FAILS CLOSED on the async
  // role — while `/session` is still loading (`roleLoaded` false) the box
  // stays hidden, so an ECL agent can never briefly see the non-ECL affordance
  // (and the recording routes stay 403'd server-side regardless — the
  // dedicated `recordRegistration2faFail` permission, granted to every SCRN-20
  // role except ECLAgent, is the authoritative gate; this visibility split is
  // only the UX surface and derives from the SERVER-resolved session role).
  const canFail2fa =
    !!config.twoFactor &&
    !!config.unableToComplete2faTickBox &&
    roleLoaded &&
    agentRole !== 'ECLAgent';
  // The selected reason; `skip2faRecorded` flips true ONLY once the BFF has
  // accepted + recorded the skip server-side (fail-closed — a refusal restores
  // the OTP requirement). `skip2faError` is the inline failure surface
  // (#80/#82/#94 inline-error rule).
  // #790 (@dylan219): the selected "reason for non-completion" must be RETAINED
  // when the agent navigates away (e.g. to the IPW reset page) and returns. It
  // is snapshotted alongside the other answer marks and restored here — critical
  // for the `2FA Exemption Approved by TL` reason, which is selected but not yet
  // recorded (`skip2faRecorded` false), so without persisting the raw selection
  // the reason would blank out on Back.
  const [skip2faReason, setSkip2faReason] = useState(
    () => restoredSnapshot?.skip2faReason ?? '',
  );
  const [skip2faRecorded, setSkip2faRecorded] = useState(
    () => restoredSnapshot?.skip2faRecorded ?? false,
  );
  const [skip2faRecording, setSkip2faRecording] = useState(false);
  const [skip2faError, setSkip2faError] = useState('');
  // #586 extension (@dylan219 2026-09-01): the "2FA Exemption Approved by TL"
  // reason additionally requires the APPROVING TEAM LEAD'S NAME — the same
  // field, validation and server-side capture as the High Security screen
  // (#586). Unlike the other reasons the TL-exemption skip is NOT recorded on
  // selection: the required field appears and the recording (submit) stays
  // blocked until a valid name is entered; the name is forwarded to the BFF
  // (which re-validates it) and captured in the audited skip record.
  // #790: the approving team lead's name for the TL-exemption reason is retained
  // across the IPW round-trip together with `skip2faReason`, so a mid-entry TL
  // exemption is not lost on Back.
  const [teamLeadName, setTeamLeadName] = useState(
    () => restoredSnapshot?.teamLeadName ?? '',
  );
  const [teamLeadNameError, setTeamLeadNameError] = useState('');
  // #789: when the reason is "2FA Customer Device Issue" the agent must tick a
  // "Troubleshooting completed" checkbox and submit before the skip is recorded
  // — mirroring the deferred (not-recorded-on-select) TL-exemption flow so an
  // un-confirmed selection never leaves the 2FA piece falsely satisfied.
  const [troubleshootingDone, setTroubleshootingDone] = useState(false);
  // Set once `/security/registration/complete` has landed (at the transfer
  // action): the run is consumed one-shot server-side, so a transfer RETRY
  // must not call `complete` again (it would 403 on the consumed run and
  // block a transfer the already-`medium` session would pass), and a
  // re-SELECTED reason after that needs a fresh run begun first.
  const [skip2faCompleted, setSkip2faCompleted] = useState(
    () => restoredSnapshot?.skip2faCompleted ?? false,
  );
  // #774 (@dylan219): the Mortgage "Unable to complete 2FA" tick box. Like the
  // skip flag above, the tick is asserted ONLY once the BFF confirms the
  // server-side recording (fail-closed — a refusal restores the ticked-off
  // state + surfaces the inline error). `twoFaFailRecordedClient` is the
  // client mirror of the SERVER marker (`twoFaFailRecorded`): seeded from the
  // #737-style 2fa-status read on mount so a re-mount re-hydrates the tick
  // from the server, never a client-only claim.
  const [unable2faTicked, setUnable2faTicked] = useState(false);
  const [unable2faRecording, setUnable2faRecording] = useState(false);
  const [unable2faError, setUnable2faError] = useState('');
  // #699 (@dylan219): registration uses a filtered subset — "Mobile Number Update"
  // and "2FA Already Completed" are not applicable at the point of registration.
  const registrationSkip2faReasons = REGISTRATION_SKIP_2FA_REASONS;
  // #586 extension: the TL-exemption selection + name-validity gating, using
  // the SAME validation as the High Security (#586) and Mobile Number Update
  // team-lead fields (Unicode letters, spaces, apostrophes, hyphens).
  const TL_NAME_ALLOWED = /^[\p{L} '-]+$/u;
  const exemptionApprovedByTl =
    skip2faReason === '2FA Exemption Approved by TL';
  const teamLeadNameValid = TL_NAME_ALLOWED.test(teamLeadName.trim());

  // #737 (@dylan219's #412 rule — the registration analogue of the #456
  // medium→high carry): when the SERVER recorded that 2FA was already
  // satisfied earlier in this session for the SAME customer (a validated OTP
  // bound to it, or a consumed sanctioned #534 skip — never the ladder level,
  // invalidated by a terminal #639 lockout), the 2FA step renders as
  // already-completed instead of re-asking the agent for a fresh OTP. Set
  // ONLY from the BFF's server-derived verdict (`/security/registration/
  // 2fa-status`) — never a client-side assumption — and re-derived fresh per
  // customer context on every mount (never persisted in the restore snapshot:
  // the server is the sole authority, mirroring `twoFaCarriedOver` in
  // `SecurityScreen.tsx`). Fail-closed: until the server confirms, the OTP
  // step stays.
  const [priorTwoFaSatisfied, setPriorTwoFaSatisfied] = useState(false);
  // One-shot guard so the status read fires ONCE per customer/Teller-ID
  // context (per-customer change resets it — a NEW customer is re-asked the
  // server, never inheriting the prior customer's verdict).
  const prior2faQueriedRef = useRef<string | null>(null);

  const [scriptingComplete, setScriptingComplete] = useState(
    () => restoredSnapshot?.scriptingComplete ?? false,
  );
  const [transferred, setTransferred] = useState(false);
  // #452 — inline error beside the (existing, post-questions + 2FA) transfer
  // button. Recoverable: the button stays clickable so the agent can retry.
  const [transferError, setTransferError] = useState('');
  // #452 — transfer in-flight state (disables the button) + a synchronous
  // re-entry guard (debounce) so a rapid double-click fires the blind transfer
  // ONCE (there was a ~4× client retry storm on this button).
  const [transferring, setTransferring] = useState(false);
  const transferInFlight = useRef(false);

  // #653 — "Reset internet password" navigation state. In-flight guard +
  // recoverable inline error for the registration → Reset Internet Password
  // (SCRN-57) round-trip. The current registration route is resolved from the
  // config's screen code (`REGISTRATION_PAGES`) so the IPW page's Back button
  // returns to EXACTLY this screen.
  const [ipwNavigating, setIpwNavigating] = useState(false);
  const ipwNavInFlight = useRef(false);
  const [ipwNavError, setIpwNavError] = useState('');
  const currentRegistrationRoute = REGISTRATION_PAGES.find(
    (p) => p.screenCode === config.screenCode,
  )?.route;

  // "Not on file" toggles (Figma `update-reg-eds`; Business24 CCF-57, issue
  // #333). They are display affordances that reveal the alternative question
  // (other products / landline) the agent asks; they never relax the
  // fail-closed Pass gate. Gated by `notOnFileToggles` (which `open24Extras`
  // implies) below.
  const [maidenNotOnFile, setMaidenNotOnFile] = useState(
    () => restoredSnapshot?.maidenNotOnFile ?? false,
  );
  const [mobileNotOnFile, setMobileNotOnFile] = useState(
    () => restoredSnapshot?.mobileNotOnFile ?? false,
  );
  const notOnFileToggles = !!(config.open24Extras || config.notOnFileToggles);
  // #774 (@dylan219, 2026-09-11) — Open24 (SCRN-17)/Business24 (SCRN-18)/VISA
  // (SCRN-19): the mobile-number question is ALWAYS directly markable Pass/Fail
  // (even with no mobile on file) and offers NO alternate question. This
  // SUPERSEDES the mobile "not on file" → landline-alternate behaviour ruled by
  // #348/#656/#662/#372: the "Mobile number not on file" tick-box + landline
  // sub-block are suppressed on these three screens, and a FAIL on the mobile
  // question auto-fails 2FA (deferred until all other questions are completed).
  //
  // #774 (@dylan219, 2026-09-11) reopen — this is RETAINED FOR ECL: Dylan ruled
  // "On ECL, please leave this exactly as it is" and, after the first fix leaked
  // into the ECL journey, "there was a change to ECL where they no longer have
  // the tick if mobile number not on file to go to the alternate question. That
  // needs to be retained for ECL Registration." The always-markable behaviour is
  // therefore scoped OUT for an ECL agent, who keeps the pre-!1130 mobile
  // "not on file" → tick → landline-alternate path (all downstream render/gate
  // paths are already guarded on `mobileAlwaysMarkable`, so nothing else
  // changes). This is a security-sensitive ID&V/registration gate, so it FAILS
  // CLOSED on the async role: until `/session` has resolved (`roleLoaded`
  // false) the flag stays OFF — the mobile "not on file" → landline-alternate
  // path is shown and the mobile Pass radio is NOT directly markable. Only once
  // the role has loaded AND is not ECL does the always-markable behaviour turn
  // on, so an ECL agent can never briefly bypass the alternate-question
  // requirement during the load window (ai-review HIGH, #774).
  const mobileAlwaysMarkable =
    !!config.mobileQuestionAlwaysMarkable &&
    roleLoaded &&
    agentRole !== 'ECLAgent';

  // Load the identity-question data once a customer + Teller ID are in context.
  useEffect(() => {
    // #653 — on a Reset-Internet-Password → Back RESTORE mount, the restored
    // snapshot IS this customer's answer state (it was consumed into the lazy
    // state initialisers above), so the cross-customer clears below must NOT run
    // this once — they would wipe the very answers the Back button is meant to
    // preserve. The guard is one-shot: it applies only on the first run of this
    // effect for a restore mount; any later customer/Teller-ID change clears as
    // usual (cross-customer-leakage protection unchanged).
    const skipResetForRestore = skipResetOnceRef.current;
    skipResetOnceRef.current = false;
    if (!skipResetForRestore) {
      // Reset the "not on file" toggles up front on every customer/Teller-ID
      // change (#348) — otherwise a toggle left ticked for a PRIOR customer
      // (whose maiden name/mobile was absent) could keep the alternative
      // question (other products / landline) showing for a NEW customer who
      // does have the value on file, even though the tick box itself is no
      // longer offered for them (mirrors the cards/joint-accounts reset
      // pattern below — cross-customer leakage).
      setMaidenNotOnFile(false);
      setMobileNotOnFile(false);
      // Clear any prior customer's additional-question selection/marks (#350) so
      // a stale selection can never leak across a customer/Teller-ID change.
      setSelectedAdditionalKeys([]);
      setSelectedAdditionalKey(null);
      setAdditionalStatus({});
      // #367: clear any dropdown selection (e.g. active Visa-debit card) so a
      // prior customer's choice can never leak across a context change.
      setAdditionalDropdownChoice({});
      // #534: clear any skip-2FA selection/recorded state so a prior customer's
      // skip can never carry across a context change (the server run is
      // customer-bound too, and a new ID&V resets it server-side).
      setSkip2faReason('');
      setSkip2faRecorded(false);
      setSkip2faError('');
      setSkip2faCompleted(false);
      // #774: clear any prior customer's "Unable to complete 2FA" tick too —
      // the server fail marker is customer-bound, and a stale tick must never
      // leak across a context change.
      setUnable2faTicked(false);
      setUnable2faError('');
      // #586 extension: drop any pending TL-exemption name on a context change
      // too — it must never leak across customers.
      setTeamLeadName('');
      setTeamLeadNameError('');
      // #737: drop any prior-2FA pre-completion on a customer/Teller-ID change
      // and let the server re-derive it for the NEW context (the evidence is
      // CIF-bound server-side — the prior customer's verdict must never carry).
      setPriorTwoFaSatisfied(false);
      prior2faQueriedRef.current = null;
    }
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
      setLoadError(true);
      return;
    }
    let cancelled = false;
    setLoadError(false);
    void (async () => {
      try {
        const { data, response } = await api.POST('/security/questions', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        });
        if (cancelled) return;
        if (!response.ok || !data) {
          setLoadError(true);
          return;
        }
        setQuestions(data as SecurityQuestionsData);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerNumber, tellerId]);

  // Open24 (issue #318): fetch the authoritative joint-accounts confirmation
  // from `/security/joint-associations`, exactly like the EDS High-Security
  // check (issue #282). Best-effort: a failure or an empty result leaves
  // `jointAccounts` undefined, so the row shows "Answer not on file" — it never
  // blocks the (none-mandatory) confirmatory check from being marked, and it
  // never falls back to the mock-only `/security/questions` value.
  useEffect(() => {
    setJointAccounts(undefined);
    if (!config.open24Extras) return;
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data, response } = await api.POST(
          '/security/joint-associations',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (!response.ok || !data) return;
        const rows = data.JointAssociations ?? [];
        const formatted = rows.map((r) => {
          const acct = r.AccountLast4 ? `••${r.AccountLast4}` : '';
          const desc = r.AccountDescription ? `${r.AccountDescription} ` : '';
          const holder = r.HolderName ? ` — ${r.HolderName}` : '';
          return `${desc}${acct}${holder}`.trim();
        });
        setJointAccounts(formatted);
      } catch {
        // Non-fatal: leave undefined -> "Answer not on file".
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config.open24Extras, customerNumber, tellerId]);

  // Issue #389: fetch the customer's ACTUAL other products/accounts from the
  // REAL gateway (`customer/accounts/list` via `/security/products-summary`) so
  // the "Mother's maiden name not on file" branch can list them — masked, e.g.
  // "Switch Current A/C ••4701" — exactly as `SecurityScreen.tsx` does, instead
  // of the generic static list. Runs for every registration screen that offers
  // the "not on file" toggles (Open24 / VISA / Business24). Best-effort: a
  // failure or an empty result leaves `otherProducts` undefined, so the prompt
  // falls back to the static `MAIDEN_FALLBACK_PRODUCTS` list and the maiden row
  // stays markable Pass/Fail (#361/#372, CCF-57 — never blocked).
  useEffect(() => {
    setOtherProducts(undefined);
    if (!notOnFileToggles) return;
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data, response } = await api.POST(
          '/security/products-summary',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (!response.ok || !data) return;
        // Issue #604 fail-safe: when the OTHER-products read was unavailable
        // the list may be incomplete (accounts-only), so it must NOT be read
        // back as the customer's authoritative full product set. Leave the
        // authoritative list empty so the prompt falls back to the static
        // `MAIDEN_FALLBACK_PRODUCTS` list rather than a partial one.
        setOtherProducts(data.ProductsUnavailable ? [] : (data.Products ?? []));
      } catch {
        // Non-fatal: leave undefined -> static fallback list.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notOnFileToggles, customerNumber, tellerId]);

  // Open24 (#407): the "Do you have an active Visa debit card?" confirmatory
  // check's on-file list, sourced from the SERVER-enriched
  // `/security/questions` `ActiveVisaDebitCards` field.
  //
  // History: #348 sourced this from the client's own Card-Maintenance `/cards`
  // fetch (below), because at that time `questions.ActiveVisaDebitCards` came
  // only from the mock-only `customer/security-questions` op (always absent
  // against the real gateway, #76). That rationale is now STALE: #345/#388
  // added a REAL, server-side `card/list` enrichment to `getSecurityQuestions`
  // (`banking/client.ts`) that populates `ActiveVisaDebitCards` from the live
  // gateway (VSDR/Activated cards, last-4 masked) — the SAME source the ECL
  // Medium/High security screens already consume.
  //
  // Crucially, the client `/cards` route is `requireVerified` (MEDIUM)-gated,
  // but the Open24 REGISTRATION flow runs at verification level `none` (the
  // customer's card is unregistered — that is WHY the agent is registering
  // them; no PAN is challenged). So the `/cards` fetch returned 403 on this
  // screen and the dropdown was ALWAYS empty/greyed — the customer's genuine
  // active Visa debit card (confirmed present on the live gateway) never
  // showed (#407). `/security/questions` is an ID&V-flow route (Teller-ID +
  // RBAC, NOT verification-gated), so its server-side enrichment runs at level
  // `none` and surfaces the card correctly. Fail-closed on
  // `ActiveVisaDebitCardsError` (a swallowed enrichment failure must never read
  // as an authoritative "no card"): show no entries so the agent cannot mark
  // the (none-mandatory) check off absent data.
  const activeVisaDebitCards = useMemo(
    () =>
      questions && !questions.ActiveVisaDebitCardsError
        ? // #527/CCF-107: the BFF enriches with the FULL 16-digit number
          // (dylan219 sign-off); render verbatim so the agent can read it back.
          (questions.ActiveVisaDebitCards ?? [])
        : [],
    [questions],
  );

  const setMandatory = (key: string, v: 'pass' | 'fail'): void =>
    setMandatoryStatus((s) => ({ ...s, [key]: v }));
  const setAdditional = (key: string, v: 'pass' | 'fail'): void =>
    setAdditionalStatus((s) => ({ ...s, [key]: v }));

  // Open24 (Figma `update-reg-eds`): when the agent ticks "Mother's maiden name
  // not on file" / "Mobile number not on file", the verifiable answer becomes
  // the ALTERNATIVE the agent then asks for — the customer's other products
  // (maiden) or the landline on file (mobile). The fail-closed gate must verify
  // against THAT alternative, so resolve the effective per-row value here. Any
  // row with no server-backed value (even after the swap) stays disabled.
  //
  // Both tick boxes are now ALWAYS offered regardless of the on-file value: the
  // MAIDEN box since #626 (branches enter unmatchable junk into the field), and
  // the MOBILE box since #662 / CCRS-3447 (@dylan219: "any security OR
  // REGISTRATION screen where the mobile question exists it should always
  // display the tick box regardless of whether information is populated or
  // not"). The no-bypass invariant is unchanged — a tick only reveals the
  // alternative question; an explicit Pass on it is still required.
  //
  // The item's raw on-file value, IGNORING any "not on file" alternative-question
  // override. `open24Alternative`/visibility no longer consult it (both ticks are
  // always shown, above), but PASSABILITY still does: the #656 "passable with no
  // server value" exception only applies when the mobile is genuinely absent (see
  // `landlineNotOnFilePassable`). When a mobile IS on file and there is no
  // landline alternative, there is nothing to verify against, so that path stays
  // fail-closed on Pass (dylan219 #348 no-bypass invariant; ai-review #662).
  const hasMandatoryOnFile = (key: string): boolean => {
    if (!questions) return false;
    const m = config.mandatory.find((x) => x.key === key);
    return !!(m && m.value(questions, customer.name));
  };

  const open24Alternative = (key: string): string | undefined => {
    if (!notOnFileToggles || !questions) return undefined;
    // #626 (@dylan219, reversing the maiden half of #348): the maiden "not on
    // file" tick is ALWAYS available, even when a maiden name IS on file —
    // branches enter junk ('unknown','TBC','Not known'…) that can't be
    // pattern-matched, so the agent must always be able to record it and verify
    // against the customer's other products instead. Mobile keeps the #348 rule.
    if (key === 'maiden' && maidenNotOnFile) {
      // Issue #372: the displayed products prompt is ALWAYS present, so the
      // agent always has something to verify the customer against and the row
      // must be markable Pass/Fail (CCF-57 "Each section requires Pass or
      // Fail"). Prefer the customer's REAL associated accounts from
      // `/security/products-summary` (#389 — the same masked-account list the
      // security screens show); then the gateway `OtherProducts`; and finally
      // the static `MAIDEN_FALLBACK_PRODUCTS` list. The gate reads the SAME
      // source the UI renders below, so the two can never drift.
      if (otherProducts && otherProducts.length > 0) {
        return otherProducts.map(formatProductEntry).join(', ');
      }
      const products = questions.OtherProducts ?? [];
      return products.length > 0
        ? products.join(', ')
        : MAIDEN_FALLBACK_PRODUCTS.join(', ');
    }
    // #662 (@dylan219 / CCRS-3447, reversing the mobile half of #348 — mirrors
    // the maiden rule above and `SecurityScreen.tsx`): the mobile "not on file"
    // tick is now ALWAYS available on registration screens, even when a mobile
    // IS on file, so the agent can always flag a stale/incorrect number and
    // verify against the landline instead. The `hasMandatoryOnFile('mobile')`
    // guard (which previously restricted this to a blank mobile) is gone — the
    // landline alternative is resolved whenever the tick is ticked.
    // #774 (@dylan219): on Open24/Business24/VISA there is NO landline alternate
    // for the mobile question (the tick-box is suppressed, so `mobileNotOnFile`
    // can never be set — the `&& !mobileAlwaysMarkable` guard is defence-in-depth).
    if (key === 'mobile' && mobileNotOnFile && !mobileAlwaysMarkable) {
      return questions.Landline || undefined;
    }
    return undefined;
  };

  // Resolve each mandatory item's on-file value. An item with NO value cannot be
  // marked Pass (fail closed): the agent has nothing server-backed to verify
  // against, so it is disabled and excluded from a passing outcome (CCF: missing
  // customer details must be handled explicitly, never silently passed).
  const mandatoryValue = (m: MandatoryItem): string | undefined => {
    if (!questions) return undefined;
    const alt = open24Alternative(m.key);
    if (alt !== undefined) return alt;
    // When an Open24 "not on file" toggle is set but the alternative is also
    // absent, fall through to undefined (fail-closed): the row is disabled.
    if (
      notOnFileToggles &&
      ((m.key === 'maiden' && maidenNotOnFile) ||
        (m.key === 'mobile' && mobileNotOnFile))
    ) {
      return undefined;
    }
    return m.value(questions, customer.name);
  };
  // #656 (@dylan219): with "Mobile number not on file" ticked and NO landline
  // on file either, "no landline on file" is itself a valid answer, so an
  // explicit Pass on that row is a legitimate passing mark even though there is
  // no server-backed value to gate on. This is the ONE identity-row exception to
  // the "must have an on-file value to pass" rule (the DOB-not-on-file case
  // stays fail-closed on Pass — its Pass radio is disabled, so it can never
  // reach a Pass mark here). Kept alongside `mandatoryValue` so the gate and the
  // rendered per-row `disablePass` can never drift.
  //
  // #662 (ai-review): now the mobile tick is always shown even when a mobile IS
  // on file, this exception is gated on the mobile being GENUINELY ABSENT. When a
  // mobile IS on file but no landline alternative resolves, there is nothing to
  // verify against — the row must stay fail-closed on Pass (no bypass), so the
  // exception does not apply and the Pass radio is disabled below.
  const landlineNotOnFilePassable = (m: MandatoryItem): boolean =>
    m.key === 'mobile' &&
    mobileNotOnFile &&
    !hasMandatoryOnFile('mobile') &&
    !mandatoryValue(m);
  const mandatoryAllPass =
    config.mandatory.length > 0 &&
    config.mandatory.every(
      // External checks (e.g. TSYS — CCF-58) have no on-file value to verify
      // against; they pass on the agent's explicit Pass mark. Identity items
      // stay fail-closed: they require an on-file value AND a Pass — except the
      // #656 landline-not-on-file case, which is passable on the explicit Pass.
      // #774 (@dylan219): on Open24/Business24/VISA the mobile row is always
      // markable — a Pass counts even when there is no mobile on file (the row
      // is verified like an externalCheck), so include that case here.
      (m) =>
        (m.externalCheck ||
          !!mandatoryValue(m) ||
          landlineNotOnFilePassable(m) ||
          (mobileAlwaysMarkable && m.key === 'mobile')) &&
        mandatoryStatus[m.key] === 'pass',
    );
  const mandatoryAnyFail = config.mandatory.some(
    (m) => mandatoryStatus[m.key] === 'fail',
  );

  const additionalAnswered = config.additional.filter(
    (q) => additionalStatus[q.key] != null,
  );
  const additionalAnyFail = additionalAnswered.some(
    (q) => additionalStatus[q.key] === 'fail',
  );
  const additionalEnough =
    selectLimit != null
      ? // Mortgage (issue #350): EXACTLY `selectLimit` questions selected AND
        // every one of those selected questions marked Pass. The selection
        // count is authoritative (not just the answered count) so a passing
        // outcome requires the agent to have actually chosen the required
        // number of questions.
        selectedAdditionalKeys.length === selectLimit &&
        selectedAdditionalKeys.every((k) => additionalStatus[k] === 'pass')
      : additionalAnswered.length >= config.requiredAdditional &&
        additionalAnswered.every((q) => additionalStatus[q.key] === 'pass');

  // #534: a server-recorded skip (ECL agents only) satisfies the 2FA piece
  // exactly like a passed OTP — mirroring the Security screens (#287).
  // #737: so does a SERVER-confirmed prior-2FA for this customer (2FA already
  // completed earlier in the session — #412 rule), which is what replaces the
  // OTP entry step with the "already completed" indicator below.
  const twoFactorOk =
    !config.twoFactor ||
    otpStatus === 'pass' ||
    skip2faRecorded ||
    priorTwoFaSatisfied;
  const anyFail = mandatoryAnyFail || additionalAnyFail;

  // #774 (@dylan219): on Open24/Business24/VISA a FAIL on the mobile-number
  // question auto-fails 2FA — there is no point sending an OTP. The fail outcome
  // is DEFERRED until every other question is completed (see showFailureBanner),
  // and the OTP "Send Code"/entry is disabled below.
  const twoFactorAutoFail =
    config.twoFactor &&
    ((mobileAlwaysMarkable && mandatoryStatus['mobile'] === 'fail') ||
      // #774 (@dylan219): on Mortgage, ticking "Unable to complete 2FA" marks
      // the 2FA piece a fail (recorded SERVER-side — this flag mirrors the
      // server's confirmation, never a client assumption), disabling the OTP
      // path; its fail outcome stays deferred like the mobile-question case.
      unable2faTicked);

  // CCF-57/CCF-58: even when a question is marked Fail the agent must still
  // proceed with 2FA, and the fail result / branch-referral is NOT shown until
  // 2FA has completed. So for two-factor journeys the question-failure banner is
  // deferred until the OTP step has resolved (pass or fail).
  const twoFactorResolved =
    otpStatus === 'pass' ||
    otpStatus === 'fail' ||
    skip2faRecorded ||
    priorTwoFaSatisfied ||
    // #774: a mobile-question FAIL is itself a resolved (failed) 2FA outcome.
    twoFactorAutoFail;
  // #781 (@philipc39, CCRS-3785): on the non-2FA Broker24 journey the failure
  // banner must NOT appear the instant one question is marked Fail — it is only
  // shown once the agent has answered ALL the questions (every mandatory row +
  // the required number of additional questions, however each was marked). This
  // is the non-2FA analogue of the 2FA journeys' deferral above (which instead
  // wait for the OTP step). Every non-Broker registration screen has 2FA, so
  // this `allQuestionsAnswered` gate is only reached for Broker24 — matching the
  // report that the premature FAIL message is isolated to Broker Registration.
  const additionalAllAnswered =
    selectLimit != null
      ? // Mortgage-style select-EXACTLY-N: all N selected questions marked.
        selectedAdditionalKeys.length === selectLimit &&
        selectedAdditionalKeys.every((k) => additionalStatus[k] != null)
      : // Broker24 select-ONE (and any plain N-of-M): the required number of
        // additional questions have a Pass/Fail mark.
        additionalAnswered.length >= config.requiredAdditional;
  const allQuestionsAnswered =
    config.mandatory.every((m) => mandatoryStatus[m.key] != null) &&
    additionalAllAnswered;
  // CCF-59: a terminal 2FA FAILURE itself prevents registration and refers the
  // customer to branch — so the referral banner shows on a failed question
  // (after 2FA resolves) OR on a terminal OTP failure, even when every question
  // passed.
  // #774 (@dylan219): when the mobile-question FAIL auto-fails 2FA, the fail
  // outcome / referral banner must NOT display until ALL other questions are
  // completed (every mandatory row marked + the required additional answered) —
  // the same deferral the non-2FA Broker24 journey uses. For every other 2FA
  // failure the existing `twoFactorResolved` (wait-for-OTP) deferral stands.
  const showFailureBanner =
    otpStatus === 'fail' ||
    // #774 (@dylan219): a ticked "Unable to complete 2FA" is itself a resolved
    // 2FA FAILURE even when every question PASSES — like a terminal OTP fail it
    // prevents registration and refers the customer to branch — so it must
    // reach the banner too. Its display is DEFERRED until every other question
    // is completed (the #781 deferral the mobile-question case above already
    // uses), so ticking alone shows nothing.
    (twoFactorAutoFail && allQuestionsAnswered) ||
    (anyFail &&
      (config.twoFactor
        ? twoFactorAutoFail
          ? allQuestionsAnswered
          : twoFactorResolved
        : allQuestionsAnswered));

  // FAIL CLOSED: the screen may only be worked when a customer is identified, a
  // valid Teller ID is present, and the customer's security-question data has
  // LOADED (no error). Until then the Pass/Fail controls, the 2FA step and the
  // transfer/manual-password actions are all disabled — an agent must never be
  // able to mark blank/unloaded questions Pass and transfer (this is a
  // security-sensitive identity flow). `questions` is non-null only after a
  // successful /security/questions response (loadError stays false).
  const dataReady =
    !!customerNumber && /^\d{4}$/.test(tellerId) && !!questions && !loadError;

  // #760 (@philipc39, SCRN-20 Mortgage / CCF-59): the OTP is delivered by SMS to
  // the customer's on-file mobile, so there must actually BE a mobile number to
  // send it to. The "Customer mobile number" line above renders exactly these
  // two fields (`MobileNumber` / `MaskedMobile`, falling back to "Not on file"),
  // so we gate the "Send Code" button on the SAME signal: a mobile is present
  // only when the payload carries a full or masked number.
  //
  // This aligns Mortgage with the other registration screens, whose mandatory
  // "Mobile number" row makes a missing mobile visible; Mortgage has no such row
  // (only name + borrower address), so without this guard "Send Code" stayed
  // clickable with no number on file and every click failed. It is orthogonal to
  // the "Mobile number not on file" VERIFICATION tick (#656/#662), which governs
  // the Pass/Fail rows, not whether an SMS OTP can be delivered.
  const hasMobileOnFile = !!(
    questions?.MobileNumber?.trim() || questions?.MaskedMobile?.trim()
  );

  // BLOCK on the J&N fraud/AML stop (issue #666, ai-review HIGH). The J&N hard
  // stop rides on the `/idv/auth-hold-check` call whose server side ALSO records
  // the authoritative `requireNoJnHoldBlock` block. There are THREE states that
  // must prevent registration progression/transfer, and `jnHoldBlocked` covers
  // all of them (fail closed):
  //  1. A CONFIRMED J&N hold (`jnHold` present) — the fraud/AML HARD STOP itself:
  //     the customer carries a J+N hold, the pop-up/banner is raised, and the
  //     agent must transfer to the fraud team, NOT complete registration. Gating
  //     only on the indeterminate states below would let the agent acknowledge
  //     the pop-up, pass every question and progress/transfer with a confirmed
  //     stop in place (the exact ai-review HIGH).
  //  2. A read that genuinely FAILED (`jnHoldError`) — the block may not have
  //     been recorded AND the pop-up cannot be raised, so we cannot confirm the
  //     absence of a stop.
  //  3. The check still IN FLIGHT / not started (`!jnHoldCheckComplete`) —
  //     `jnHoldError` is only set once the read FAILS and stays false during the
  //     loading window, so gating on it alone would let the agent progress BEFORE
  //     the check confirms the absence of a hold.
  // This is distinct from the CCF-43 auth-hold pop-up, which keeps its
  // story-mandated fail-open.
  const jnHoldBlocked = !!jnHold || jnHoldError || !jnHoldCheckComplete;

  // Issue #834 — the CCF-43 AML (H/I/O) HARD STOP. When the auth-hold check
  // CONFIRMS `blocked` (no genuinely ACTIVE account free of an H/I/O hold), the
  // pop-up offers Close only — and closing it must NOT let registration
  // continue: no PASS badge, no mandatory scripting / transfer, no internet
  // password reset. Only a confirmed `blocked` verdict stops; a hold-check
  // ERROR keeps its CCF-43 fail-open contract (#67) for the auth-hold portion.
  // The BFF enforces the same stop server-side (`requireNoAuthHoldBlock`).
  // Online-only by construction: the hook never runs the check offline.
  const authHoldHardStop = authHold?.blocked === true;

  // CCF-318 Unhappy Path #2 — insufficient data on file (#772, @dylan219),
  // Broker24 (SCRN-21) only. When the broker has NO security questions on file
  // (the free-text `AdditionalQuestionAnswers` list has no distinct answers)
  // there is nothing to verify the additional questions — or the eye-colour /
  // first-school external-check rows, judged against that SAME shared list
  // (#349) — against, so security cannot be completed. Show an insufficient-data
  // note and block a passing outcome. Scoped by `insufficientDataWhenNoAnswers`
  // so Business24's deliberately always-markable empty-field behaviour (#648) is
  // untouched.
  const noQuestionsOnFile =
    !!config.insufficientDataWhenNoAnswers &&
    dataReady &&
    countDistinctAnswers(questions?.AdditionalQuestionAnswers) === 0;

  // Security passes when the data is ready, every mandatory item is Pass, the
  // required number of additional questions are answered + all passed, 2FA (if
  // any) passed, and the J&N fraud/AML check is NOT blocked. `dataReady` is
  // REQUIRED so security can never pass on unloaded/failed data; `!jnHoldBlocked`
  // is required so it can never pass with a confirmed or unconfirmed J&N stop.
  const securityPass =
    dataReady &&
    mandatoryAllPass &&
    additionalEnough &&
    twoFactorOk &&
    !anyFail &&
    !jnHoldBlocked &&
    !authHoldHardStop &&
    !noQuestionsOnFile;

  // CCF-112 (#477): record the registration screen's terminal FAIL outcome
  // ("Unable to complete registration" — the customer failed the security
  // questions / 2FA and is referred to branch, the `showFailureBanner` state) as
  // a Non-API business event in Call History. The fail outcome is a pure
  // client-side screen state (no gateway call), so — mirroring the security-event
  // (MediumSecurityScreen) and email-template (#434) events — it is fired
  // best-effort here from the client, exactly once per terminal outcome. The
  // registration PASS side is recorded by the sibling effect below (#855) under
  // the SAME `Registration Result` event name, so Admin Reporting (CCF-113) shows
  // a true pass/fail split; the IVR hand-off is a separate event ("Transferred
  // to IVR for Registration"). A once-guard keyed to the current context (customer + Teller ID) prevents a
  // re-fire on re-render; it resets when the context changes so a fresh customer
  // on the same screen is recorded again. Never breaks the flow on a
  // history-write failure.
  //
  // Stale-outcome-crosses-customers guard (#243/#244/#422 bug class): requiring
  // `!!customerNumber && !!questions` ties the outcome to the customer in
  // context — `questions` is this customer's `/security/questions` payload, which
  // is null on the first render of a new context and stays null until that
  // customer's own load resolves; `showFailureBanner` is unreachable without it
  // (`dataReady` requires `questions`), so the effect provably cannot fire a
  // prior customer's outcome against a newly-swapped CIF.
  const registrationEventFiredRef = useRef<string | null>(null);
  useEffect(() => {
    const contextKey = `${customerNumber}|${tellerId}`;
    // Reset the once-guard when the context changes so the next customer's
    // terminal fail is recorded (mirrors the per-customer reset pattern above).
    if (registrationEventFiredRef.current !== null) {
      const [firedCif] = registrationEventFiredRef.current.split('|');
      if (registrationEventFiredRef.current !== contextKey && firedCif) {
        registrationEventFiredRef.current = null;
      }
    }
    if (registrationEventFiredRef.current === contextKey) return;
    if (!customerNumber || !questions) return;
    if (!showFailureBanner) return;
    registrationEventFiredRef.current = contextKey;
    void api
      .POST('/calls/registration-event', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          outcome: 'Unable to complete registration',
        },
      })
      .catch(() => {
        // Best-effort: a Call-History write failure must never break the flow.
      });
  }, [showFailureBanner, customerNumber, tellerId, questions]);

  // #855 (CCF-112/113): record the registration security PASS verdict as a
  // `Registration Result` / `Passed` Call-History event, so Admin Reporting's
  // Registration Result row reflects passes as well as fails (previously only
  // the fail side was recorded, so the row read 100% failed by construction).
  // Fired best-effort, exactly once per customer/Teller-ID context, when the
  // shared registration engine reaches its PASS verdict (`securityPass`). Same
  // discipline as the fail effect above: a once-guard ref that resets on a
  // context change, and `!!customerNumber && !!questions` ties the verdict to
  // the customer in context (`securityPass` requires `dataReady`, which requires
  // this customer's own `questions`), so a prior customer's pass can never be
  // recorded against a newly-swapped CIF. Never breaks the flow.
  const registrationPassFiredRef = useRef<string | null>(null);
  useEffect(() => {
    const contextKey = `${customerNumber}|${tellerId}`;
    if (registrationPassFiredRef.current !== null) {
      const [firedCif] = registrationPassFiredRef.current.split('|');
      if (registrationPassFiredRef.current !== contextKey && firedCif) {
        registrationPassFiredRef.current = null;
      }
    }
    if (registrationPassFiredRef.current === contextKey) return;
    if (!customerNumber || !questions) return;
    if (!securityPass) return;
    registrationPassFiredRef.current = contextKey;
    void api
      .POST('/calls/registration-event', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, outcome: 'Passed' },
      })
      .catch(() => {
        // Best-effort: a Call-History write failure must never break the flow.
      });
  }, [securityPass, customerNumber, tellerId, questions]);

  function ctxOk(): boolean {
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
      toast.error('No customer in context. Complete ID&V first.');
      return false;
    }
    return true;
  }

  // #534 — begin the server-side Registration Security run once the 2FA step
  // is available to an ECL agent (customer identified + question data loaded),
  // so a later sanctioned skip is bound to a genuine registration flow.
  // Best-effort: the server still fail-closes at skip/complete if the run was
  // not recorded, and the OTP path is unaffected by a failure here.
  // #774 (@dylan219 reopen): the non-ECL tick-box flow needs an ACTIVE run for
  // the fail record too, and it begins through the dedicated
  // `recordRegistration2faFail` seam (`/begin-fail-2fa`) — the ECL dropdown's
  // `/begin` (the `skipRegistration2fa` trio) is untouched.
  useEffect(() => {
    if (!dataReady) return;
    if (canSkip2fa) {
      void beginRegistrationSecurity(customerNumber, tellerId);
    } else if (canFail2fa) {
      void beginRegistrationFail2fa(customerNumber, tellerId);
    }
  }, [canSkip2fa, canFail2fa, dataReady, customerNumber, tellerId]);

  // #737 — ask the SERVER (once per customer/Teller-ID context) whether 2FA
  // was already satisfied for this customer earlier in the session (#412 rule
  // — "don't make the agent redo them", the registration analogue of the #456
  // medium→high carry). ONLY a `true` verdict from the BFF (derived from the
  // session's server-observed 2FA markers for the BOUND customer, invalidated
  // by a #639 lockout) may replace the OTP step with the already-completed
  // indicator; any refusal / network error keeps the step asking, fail-closed.
  // The customer is provably the bound one by the time we ask: `dataReady`
  // requires this customer's `/security/questions` load to have SUCCEEDED, and
  // that route is identified-customer bound server-side. A screen re-mount on
  // return (navigation away/back) re-runs this read, so a 2FA completed on
  // another screen in the meantime is picked up without polling.
  useEffect(() => {
    if (!config.twoFactor || !dataReady) return;
    const contextKey = `${customerNumber}|${tellerId}`;
    if (prior2faQueriedRef.current === contextKey) return;
    prior2faQueriedRef.current = contextKey;
    let cancelled = false;
    void (async () => {
      const status = await checkRegistration2faStatus(customerNumber, tellerId);
      if (cancelled) return;
      // FAIL CLOSED: only ever RAISE the indicator on a server `true` — never
      // clear an in-run OTP `pass` / a server-recorded skip the agent earned
      // on THIS screen.
      if (status.satisfied) setPriorTwoFaSatisfied(true);
      // #774: re-hydrate the Mortgage "Unable to complete 2FA" tick from the
      // SERVER's run marker (never a client claim) so a screen re-mount after
      // ticking keeps the deferred-fail state.
      if (config.unableToComplete2faTickBox && status.twoFaFailRecorded) {
        setUnable2faTicked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `config.twoFactor` / `config.unableToComplete2faTickBox` are stable per
    // screen, so they never re-trigger this effect — listed only to satisfy
    // exhaustive-deps.
  }, [
    config.twoFactor,
    config.unableToComplete2faTickBox,
    dataReady,
    customerNumber,
    tellerId,
  ]);

  /**
   * #534 — the ECL agent selected (or cleared) a "reason for non-completion".
   * The skip is recorded SERVER-SIDE immediately (fail-closed): the 2FA piece
   * is treated as passed ONLY once the BFF confirms the recording — mirroring
   * the Security screens' "passed as soon as the agent selects a reason to
   * skip" (#287) but with the server confirmation required up front (the #403
   * client-only-record lesson). Clearing the selection restores the OTP
   * requirement; the server-side `medium` elevation is deferred to the
   * explicit transfer (commit) action, so a cleared selection leaves the
   * server session untouched.
   */
  /**
   * #774 (@dylan219): the agent ticked (or unticked) "Unable to complete 2FA"
   * beside Send Code on the Mortgage 2FA step. Ticking records the 2FA FAIL
   * SERVER-SIDE (fail-closed — the tick asserts only on the BFF's `recorded`
   * confirmation, mirroring the #534 skip recording); unticking clears the
   * server marker and restores the OTP path. The fail OUTCOME/messaging stays
   * DEFERRED until every other question is completed (see showFailureBanner —
   * the #781 deferral), so ticking alone shows no banner.
   */
  async function handleUnable2faToggle(tick: boolean): Promise<void> {
    setUnable2faError('');
    if (tick) {
      setUnable2faRecording(true);
      // The run is begun best-effort by the effect above; if it was consumed
      // or never landed (a prior complete), begin a fresh one first so the
      // fail records against an ACTIVE run (mirrors the skip route's
      // `skip2faCompleted` re-begin). #774 reopen: the non-ECL tick-box flow
      // re-begins through its own `recordRegistration2faFail` seam; the ECL
      // dropdown's ECL-only `/begin` is used only on the ECL path.
      if (skip2faCompleted) {
        if (canFail2fa) {
          await beginRegistrationFail2fa(customerNumber, tellerId);
        } else {
          await beginRegistrationSecurity(customerNumber, tellerId);
        }
        setSkip2faCompleted(false);
      }
      const recorded = await recordRegistrationFail2fa(
        customerNumber,
        tellerId,
      );
      setUnable2faRecording(false);
      if (!recorded) {
        // Fail-closed: the tick is NOT left asserted — without the server
        // record there is no 2FA fail, and the OTP path stays live (#729).
        setUnable2faTicked(false);
        setUnable2faError(
          'Could not record the 2FA failure. Please try again.',
        );
        return;
      }
      setUnable2faTicked(true);
      return;
    }
    // Unticked — clear the server-side marker; the OTP path is restored. The
    // tick stays asserted until the CLEAR lands (fail-closed both ways: a
    // stale server fail must not be silently trusted as cleared).
    setUnable2faRecording(true);
    const cleared = await clearRegistrationFail2fa(customerNumber, tellerId);
    setUnable2faRecording(false);
    if (!cleared) {
      setUnable2faError('Could not clear the 2FA failure. Please try again.');
      return;
    }
    setUnable2faTicked(false);
  }

  async function handleSkip2faSelect(reason: string): Promise<void> {
    setSkip2faError('');
    if (!reason) {
      // Cleared — restore the OTP requirement. #586 extension: also drop any
      // pending (un-recorded) team lead's name.
      setSkip2faReason('');
      setSkip2faRecorded(false);
      setTeamLeadName('');
      setTeamLeadNameError('');
      setTroubleshootingDone(false);
      return;
    }
    if (requiresTroubleshootingConfirmation(reason)) {
      // #789: like the TL-exemption reason, the "2FA Customer Device Issue"
      // skip is NOT recorded on selection. The "Troubleshooting completed"
      // checkbox appears and the recording is submitted via the confirm action
      // below. An earlier recorded skip (e.g. switched reason) is UNDONE here so
      // an un-confirmed selection never leaves the 2FA piece falsely satisfied.
      setSkip2faReason(reason);
      setSkip2faRecorded(false);
      setTeamLeadName('');
      setTeamLeadNameError('');
      setTroubleshootingDone(false);
      return;
    }
    if (reason === '2FA Exemption Approved by TL') {
      // #586 extension: unlike the other reasons this skip is NOT recorded on
      // selection — the required Team lead's name field appears (mirroring the
      // High Security screen) and the recording is submitted via the confirm
      // action below, with a server-validated name. An earlier recorded skip
      // (e.g. switched reason) is UNDONE here so an un-recorded TL selection
      // can never leave the 2FA piece falsely satisfied.
      setSkip2faReason(reason);
      setSkip2faRecorded(false);
      setTeamLeadName('');
      setTeamLeadNameError('');
      setTroubleshootingDone(false);
      return;
    }
    // A non-TL reason — clear any stale team lead's name so it is never
    // forwarded with a different reason (mirrors the SecurityScreen).
    setTeamLeadName('');
    setTeamLeadNameError('');
    setTroubleshootingDone(false);
    setSkip2faRecording(true);
    // A previous `complete` consumed the run one-shot (e.g. the transfer then
    // failed `not_online`): begin a FRESH run before recording again, or the
    // skip would 403 on the consumed run.
    if (skip2faCompleted) {
      await beginRegistrationSecurity(customerNumber, tellerId);
      setSkip2faCompleted(false);
    }
    const recorded = await recordRegistrationSkip2fa(
      customerNumber,
      tellerId,
      reason,
    );
    setSkip2faRecording(false);
    if (!recorded) {
      // FAIL CLOSED: the BFF refused the skip (no active run / unsanctioned
      // reason / not an ECL agent / network) — the OTP requirement stays.
      setSkip2faReason('');
      setSkip2faRecorded(false);
      setSkip2faError(
        'The reason for non-completion could not be recorded. Complete 2FA with a one-time passcode.',
      );
      return;
    }
    setSkip2faReason(reason);
    setSkip2faRecorded(true);
  }

  /**
   * #586 extension — submit the TL-exemption skip with the approving team
   * lead's name. Gated client-side on a valid name (disabled until then) and
   * RE-VALIDATED server-side; the name is captured in the audited registration
   * skip record. Fail-closed like the selection path: a refusal restores the
   * OTP requirement and resets the selection.
   */
  async function handleTlExemptionConfirm(): Promise<void> {
    if (!teamLeadNameValid) return;
    setSkip2faError('');
    setSkip2faRecording(true);
    // Same consumed-run logic as the selection path: a previous `complete`
    // consumed the run one-shot — begin a FRESH run before recording again.
    if (skip2faCompleted) {
      await beginRegistrationSecurity(customerNumber, tellerId);
      setSkip2faCompleted(false);
    }
    const recorded = await recordRegistrationSkip2fa(
      customerNumber,
      tellerId,
      '2FA Exemption Approved by TL',
      teamLeadName.trim(),
    );
    setSkip2faRecording(false);
    if (!recorded) {
      // FAIL CLOSED: the BFF refused the skip (invalid name / no active run /
      // not an ECL agent / network) — the OTP requirement stays.
      setSkip2faReason('');
      setSkip2faRecorded(false);
      setTeamLeadName('');
      setTeamLeadNameError('');
      setSkip2faError(
        'The reason for non-completion could not be recorded. Complete 2FA with a one-time passcode.',
      );
      return;
    }
    setSkip2faRecorded(true);
  }

  /**
   * #789 — submit the "2FA Customer Device Issue" skip once the agent has
   * confirmed troubleshooting was completed (the mandatory checkbox). Gated
   * client-side on the checkbox (disabled until then) and fail-closed like the
   * selection path: a refusal restores the OTP requirement and resets the
   * selection + confirmation.
   */
  async function handleTroubleshootingConfirm(): Promise<void> {
    if (!troubleshootingDone) return;
    setSkip2faError('');
    setSkip2faRecording(true);
    // Same consumed-run logic as the selection path: a previous `complete`
    // consumed the run one-shot — begin a FRESH run before recording again.
    if (skip2faCompleted) {
      await beginRegistrationSecurity(customerNumber, tellerId);
      setSkip2faCompleted(false);
    }
    const recorded = await recordRegistrationSkip2fa(
      customerNumber,
      tellerId,
      CUSTOMER_DEVICE_REASON,
      // No team lead's name for this reason.
      undefined,
      // #789: forward the "Troubleshooting completed" attestation so the server
      // can fail-closed require it (the checkbox is client-side UX only).
      troubleshootingDone,
    );
    setSkip2faRecording(false);
    if (!recorded) {
      // FAIL CLOSED: the BFF refused the skip (no active run / unsanctioned
      // reason / not an ECL agent / network) — the OTP requirement stays.
      setSkip2faReason('');
      setSkip2faRecorded(false);
      setTroubleshootingDone(false);
      setSkip2faError(
        'The reason for non-completion could not be recorded. Complete 2FA with a one-time passcode.',
      );
      return;
    }
    setSkip2faRecorded(true);
  }

  async function handleGenerateOtp(): Promise<void> {
    if (!ctxOk()) return;
    setGenerating(true);
    try {
      const { data, error, response } = await api.POST(
        '/security/otp/generate',
        {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        },
      );
      if (response.status === 403) {
        // #788 (CCF-16) — a terminal OTP lockout (#639) fails closed with
        // 403 `{ error: 'otp_locked_out' }`. Surface the real lockout message
        // INLINE beside Send Code (#782 `otpError` path), not the generic
        // RBAC/teller toast.
        if (isOtpLockedOut(response.status, error)) {
          setOtpError({ message: OTP_LOCKED_OUT_MESSAGE, source: 'generate' });
          toast.error(OTP_LOCKED_OUT_MESSAGE);
          return;
        }
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      const body = (data ?? { sent: false }) as OtpGenerateResult;
      if (body.sent) {
        setOtpSent(true);
        setOtpStatus(null);
        setOtpCode('');
        // A freshly issued code: clear any prior wrong-code warning + stale
        // attempt count so the retry surface starts clean.
        setOtpError(null);
        setOtpAttemptsRemaining(null);
        toast.success(
          body.maskedMobile
            ? `Code sent to ${body.maskedMobile}`
            : 'One-time passcode sent',
        );
      } else if (body.blocked) {
        toast.error(body.message ?? 'OTP generation is blocked.');
      } else {
        // Send Code (generate) failed — surface the gateway code + message
        // INLINE under the Send Code button (#782), not only as a toast.
        const message = body.message ?? 'Could not send the one-time passcode.';
        setOtpError({ code: body.resultCode, message, source: 'generate' });
        toast.error(message);
      }
    } catch {
      const message = 'Request failed.';
      setOtpError({ message, source: 'generate' });
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleValidateOtp(): Promise<void> {
    if (!ctxOk()) return;
    if (!/^\d{6}$/.test(otpCode)) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setValidating(true);
    // Clear any prior inline warning at the start of a fresh validate attempt.
    setOtpError(null);
    try {
      const { data, response } = await api.POST('/security/otp/validate', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, otp: otpCode },
      });
      if (response.status === 403) {
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      const body = (data ?? { valid: false }) as OtpValidateResult;
      if (typeof body.attemptsRemaining === 'number') {
        setOtpAttemptsRemaining(body.attemptsRemaining);
      }
      if (body.valid) {
        setOtpStatus('pass');
        setOtpError(null);
        toast.success('Two-factor authentication passed.');
      } else if (body.expired) {
        // The 5-minute window elapsed — retryable, not a terminal failure.
        // #810: the BFF's mapped ruled copy ("Your OTP has expired. Please
        // request a new OTP and try again.") renders INLINE under the Validate
        // button (#782 placement) and replaces the fixed toast copy when present.
        const expiredMessage =
          body.message ??
          'The one-time passcode has expired. Generate a new code.';
        setOtpSent(false);
        setOtpError({ message: expiredMessage, source: 'validate' });
        toast.error(expiredMessage);
      } else if (body.blocked) {
        // 3 incorrect attempts — only NOW does 2FA fail terminally (FAIL banner).
        setOtpStatus('fail');
        setOtpSent(false);
        setOtpError(null);
        toast.error(body.message ?? 'Incorrect code — OTP now blocked.');
      } else {
        // A non-final wrong code (issue #375): DO NOT latch a fail (which would
        // prematurely show the branch-referral) — surface the gateway code +
        // message INLINE beside the Validate button as a non-blocking warning
        // (per @dylan219's #80/#82/#94 rule) and leave the input + Validate
        // enabled so the agent can re-enter the code, mirroring
        // `SecurityScreen.tsx`. KEEP the toast as well.
        const remaining = body.attemptsRemaining;
        const message =
          body.message ??
          (typeof remaining === 'number'
            ? `Incorrect code. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
            : 'The code does not match. Please retry.');
        setOtpError({ code: body.resultCode, message, source: 'validate' });
        toast.error(message);
      }
    } catch {
      const message = 'Request failed.';
      setOtpError({ message, source: 'validate' });
      toast.error(message);
    } finally {
      setValidating(false);
    }
  }

  /**
   * #452 — the REAL Genesys blind transfer of the caller to the registration
   * self-service flow. This is the EXISTING button that appears only once all
   * security questions pass AND 2FA completes (`securityPass`) and the agent
   * ticks "Scripting complete". On click the BFF attaches `Verification=Pan` +
   * `Open24Nr` to the live conversation and blind-transfers the caller to the
   * registration self-service DID (`+353 1 1000001`). Results surface INLINE
   * beside the button (#80/#82/#94); the button stays clickable (recoverable).
   * The BFF independently enforces online-only + RBAC + participant-auth + PAN
   * and surfaces `not_online` inline.
   */
  async function handleTransfer(): Promise<void> {
    // #452 — click DEBOUNCE: drop a re-entrant click (double-click within the
    // same tick, before the disabled state paints) so the transfer fires once.
    if (transferInFlight.current || transferred) return;
    if (!securityPass) {
      toast.error('Complete all security checks before transferring.');
      return;
    }
    if (!scriptingComplete) {
      toast.error('Tick "Scripting complete" before transferring.');
      return;
    }
    transferInFlight.current = true;
    setTransferring(true);
    const open24Nr = customer.onlineCardNumber ?? '';
    setTransferError('');
    try {
      // #534 — the skip path validated no OTP, so nothing has raised the
      // server-side session to `medium` yet and the PAN-gated transfer below
      // would 403. Commit the registration completion FIRST (the BFF consumes
      // the server-recorded skip marker and raises the session) and ABORT the
      // transfer if it does not land — fail-closed, mirroring the ECL Medium
      // "complete on the explicit continue action" pattern (#403).
      if (skip2faRecorded && otpStatus !== 'pass' && !skip2faCompleted) {
        const completed = await completeRegistrationSecurity(
          customerNumber,
          tellerId,
        );
        if (!completed) {
          setTransferError(
            'The 2FA skip could not be completed. Complete 2FA with a one-time passcode and try again.',
          );
          return;
        }
        setSkip2faCompleted(true);
      }
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/genesys/transfer-to-registration', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: open24Nr ? { open24Nr } : {},
      });
      // #611 — an ONLINE-ONLY action refused ONLY because the state resolved
      // Offline (the #628 `onlineWouldAllow` signal) is a connectivity/offline
      // problem, NOT a role-level permission gap. Show the connectivity/offline
      // copy — never the permission copy (@dylan219, 2026-08-13). A genuine
      // role-level `forbidden` (no `onlineWouldAllow`) keeps the permission copy.
      if (isOnlineOnlyOfflineDenial(response.status, apiError)) {
        setTransferError(CONNECTIVITY_OR_OFFLINE_MESSAGE);
        return;
      }
      if (isActionForbidden(response.status, apiError)) {
        setTransferError(ACTION_DENIED_MESSAGE);
        return;
      }
      if (apiError || !data || (data as { ok?: boolean }).ok !== true) {
        // #731 (@dylan219) — mirror the failure (incl. the BFF's `detail`
        // diagnostic) to the browser console so the tester's console-log
        // capture carries it; the body is already in the HAR/network capture
        // now that the BFF returns `detail` in the 502/503 bodies.
        logSelfServeTransferFailure(response.status, apiError);
        // #611 — surface the SPECIFIC BFF failure reason (with a `(ref: …)` tag)
        // so the agent can see exactly what error occurred, not a generic
        // catch-all. Non-blocking — the button stays so the agent can retry.
        setTransferError(
          selfServeTransferErrorMessage(response.status, apiError),
        );
        return;
      }
      setTransferred(true);
      toast.success('Customer transferred to automated self-service.');
    } catch {
      setTransferError('Could not transfer to self-service. Please try again.');
    } finally {
      transferInFlight.current = false;
      setTransferring(false);
    }
  }

  /**
   * #653 (@dylan219) — navigate to the Reset Internet Password page (SCRN-57) to
   * reset the customer's IPW. Offered ONLY once every registration security
   * question (+ 2FA where the screen has one) has passed (`securityPass`) — completion of
   * the registration questions is treated as sufficient security, mirroring the
   * broker security → IPW option (#568).
   *
   * Steps (fail-closed):
   *  1. Record registration completion server-side (`/security/registration/
   *     complete-ipw`) — raises the session to `medium` and writes the durable,
   *     CIF-bound `registrationComplete` marker the IPW-reset write is gated on.
   *     If it does not land, ABORT (never navigate to a page whose reset would
   *     then 403).
   *  2. Snapshot this screen's answers to sessionStorage so the IPW page's Back
   *     button can restore them (the Pass — the server-side `medium` — persists
   *     independently, so 2FA is never re-done).
   *  3. Navigate with router state `{ from }` so SCRN-57 shows the scoped Back
   *     button (returning here) and hides every other navigation affordance.
   */
  async function handleResetInternetPassword(): Promise<void> {
    if (ipwNavInFlight.current) return;
    if (!securityPass) {
      toast.error('Complete all security checks first.');
      return;
    }
    if (!currentRegistrationRoute) return;
    ipwNavInFlight.current = true;
    setIpwNavigating(true);
    setIpwNavError('');
    try {
      const authorised = await completeRegistrationForIpw(
        customerNumber,
        tellerId,
        config.screenCode,
      );
      if (!authorised) {
        setIpwNavError(
          'Could not open the internet-password reset. Please try again.',
        );
        return;
      }
      saveRegistrationAnswers(config.screenCode, customerNumber, {
        mandatoryStatus,
        additionalStatus,
        additionalDropdownChoice,
        selectedAdditionalKey,
        selectedAdditionalKeys,
        otpStatus,
        scriptingComplete,
        maidenNotOnFile,
        mobileNotOnFile,
        skip2faRecorded,
        skip2faCompleted,
        // #790 — retain the selected "reason for non-completion" (+ any TL name)
        // across the IPW round-trip so it repopulates on Back.
        skip2faReason,
        teamLeadName,
      });
      navigate(RESET_INTERNET_PASSWORD_ROUTE, {
        state: {
          from: currentRegistrationRoute,
          fromScreenCode: config.screenCode,
        },
      });
    } catch {
      setIpwNavError(
        'Could not open the internet-password reset. Please try again.',
      );
    } finally {
      ipwNavInFlight.current = false;
      setIpwNavigating(false);
    }
  }

  const noContext = !customerNumber;

  return (
    <main
      className="max-w-[900px] pl-12 pr-12 py-12"
      aria-labelledby="registration-heading"
    >
      <h1
        id="registration-heading"
        className="text-[24px] font-['Arial',sans-serif] font-bold text-[#FF703E] m-0 mb-6"
      >
        {config.heading}
      </h1>

      {noContext && (
        <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3 mb-6">
          <img
            src={infoIcon}
            alt=""
            aria-hidden="true"
            className="w-5 h-5 mt-1 shrink-0"
          />
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
            No customer is in context. Identify and deregister the customer on
            the ID&amp;V screen first; the agent is then brought here
            automatically.
          </p>
        </div>
      )}

      {loadError && !noContext && (
        <div
          role="alert"
          className="bg-white border-2 border-[#dc2626] p-6 shadow-sm mb-6"
        >
          <p className="text-[16px] font-['Arial',sans-serif] text-[#dc2626] m-0">
            Unable to load the customer&apos;s security details. Retry, or
            escalate if the problem persists.
          </p>
        </div>
      )}

      {/* Issue #666 (ai-review HIGH) — the J&N fraud/AML stop FAILS CLOSED. When
          the `/idv/auth-hold-check` call (which also records the authoritative
          server-side stop) could not be completed we cannot confirm the absence
          of a J&N hold, so registration progression/transfer is blocked
          (`securityPass` includes `!jnHoldBlocked`) and this error is
          surfaced. Distinct from the CCF-43 auth-hold pop-up, which fails open.
          Gated on `jnHoldError` (the read genuinely FAILED), NOT the broader
          `jnHoldBlocked` — the still-in-flight/pending window also blocks
          progression but must not surface this error text while the check is
          merely loading (the Pass/Fail + transfer controls stay disabled via
          `securityPass` until it resolves). */}
      {/* Issue #834 — persistent CCF-43 hard-stop banner. Stays after the
          agent closes the "Account access restricted" pop-up so the stop is
          visible; `securityPass` is false while it shows (no PASS badge,
          scripting, transfer or internet-password reset). */}
      {authHoldHardStop && !noContext && (
        <div
          role="alert"
          aria-live="assertive"
          data-testid="auth-hold-hard-stop"
          className="bg-white border-2 border-[#dc2626] p-6 shadow-sm mb-6"
        >
          <div className="space-y-2">
            <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0">
              Account access restricted
            </h2>
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1D252C] m-0 leading-[1.5]">
              This customer has an account hold that restricts access.
              Registration cannot continue. Select Review hold details for the
              scripting to read to the customer.
            </p>
            <Button
              type="button"
              onClick={openAuthHold}
              aria-label="Review authentication hold details"
              className="bg-white text-[#1d252c] border-2 border-[#1d252c] rounded-md px-4 py-2 h-auto font-['Arial',sans-serif] font-bold text-[14px]"
            >
              Review hold details
            </Button>
          </div>
        </div>
      )}

      {jnHoldError && !noContext && (
        <div
          role="alert"
          className="bg-white border-2 border-[#dc2626] p-6 shadow-sm mb-6"
        >
          <p className="text-[16px] font-['Arial',sans-serif] text-[#dc2626] m-0">
            The fraud and AML hold check could not be completed for this
            customer, so registration cannot continue. Retry, or escalate if the
            problem persists.
          </p>
        </div>
      )}

      {/* Insufficient-data unhappy path (CCF-318 #2, #772): Broker24 has no
          security questions on file, so security cannot be completed. Advise
          the broker to contact their Broker Representative / IMC. */}
      {noQuestionsOnFile && (
        <div
          role="alert"
          data-testid="missing-data-note"
          className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3 mb-6"
        >
          <img
            src={infoIcon}
            alt=""
            aria-hidden="true"
            className="w-5 h-5 mt-1 shrink-0"
          />
          <div>
            <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
              There is not enough information on file to complete security.
            </p>
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
              Advise the broker to contact their Broker Representative / IMC.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Mandatory identity items. Issue #651 (supervisor screenshot): the
            section header is a dark badge bar sitting ABOVE the white info box
            (matching the Security screens), not a plain dark-text heading
            inside the box. */}
        <div className="space-y-3">
          <div className="bg-[#1D252C] text-white px-6 py-3 inline-block">
            <h2 className="text-[16px] font-['Arial',sans-serif] font-bold m-0">
              Mandatory selection
            </h2>
          </div>
          <InfoBox>
            <div className="space-y-6">
              {config.mandatory.map((m, idx) => {
                // A mandatory item with no on-file value is disabled and shown as
                // "not on file" — the agent can never mark a blank identity item
                // Pass (fail closed); it also blocks a passing outcome above.
                // EXCEPTION: an external check (TSYS — CCF-58) intentionally has
                // no on-file value; it shows only the question + a "Check TSYS…"
                // hint and stays markable, since the agent verifies it externally.
                const isExternal = !!m.externalCheck;
                const value = mandatoryValue(m);
                const hasValue = !!value;
                // Open24 (Figma) + Business24 (CCF-57, issue #333): the maiden +
                // mobile rows carry a "not on file" checkbox; when ticked the
                // agent verifies against the ALTERNATIVE question (other products
                // / landline). #626 (dylan219): the MAIDEN tick box is ALWAYS
                // offered — even when a maiden name is on file — because branches
                // enter junk values the agent must be able to flag as not-on-file.
                // #662 (@dylan219 / CCRS-3447, reversing the mobile half of #348):
                // the MOBILE tick box is now ALSO always offered — even when a
                // mobile IS on file — so the agent can flag a stale/incorrect
                // number and verify against the landline, exactly as the shared
                // HIGH-security family does. The no-bypass invariant is preserved:
                // ticking alone never passes the row (`mandatoryValue` still
                // requires the landline alternative, and the row's Pass/Fail gate
                // is unchanged).
                const isMaiden = notOnFileToggles && m.key === 'maiden';
                // #774 (@dylan219): on Open24/Business24/VISA the mobile row is
                // ALWAYS directly markable and offers NO alternate question, so
                // the "Mobile number not on file" tick-box + landline sub-block
                // are suppressed (they render only when `isMobile`). This
                // supersedes the #348/#656/#662 mobile-not-on-file behaviour.
                const isMobile =
                  notOnFileToggles &&
                  m.key === 'mobile' &&
                  !mobileAlwaysMarkable;
                // #657 (@dylan219): switching to an alternate question must NOT
                // hide the original. The QuestionRow keeps the ORIGINAL question
                // label (`m.label`) visible at all times; when the "not on file"
                // toggle is ticked, the alternative question the agent then asks
                // (other products / landline) is rendered as its own clearly
                // labelled sub-block BELOW the checkbox, never by overwriting the
                // original label. The Pass/Fail control on the original row records
                // the agent's verification against that alternative.
                const showMaidenAlt = isMaiden && maidenNotOnFile;
                const showMobileAlt = isMobile && mobileNotOnFile;
                // #656 (@dylan219, authoritative — narrows the fail-closed default
                // of #348/#333/#626 for exactly two not-on-file rows; every other
                // row keeps both marks disabled when there is nothing on file):
                //
                //  1. Landline confirmation with NO landline on file → allow BOTH
                //     Pass and Fail. Once the agent ticks "Mobile number not on
                //     file" the row's question becomes "Can you confirm your
                //     landline number?"; if no landline is on file either, "no
                //     landline on file" is itself a valid answer, so the agent
                //     must be able to mark Pass OR Fail rather than the row being
                //     stuck disabled.
                //  2. Date of birth NOT on file → allow FAIL only (Pass stays
                //     fail-closed): the agent can record that the customer failed
                //     the DOB check, but can never mark a blank DOB Pass.
                //  3. #662 (ai-review): mobile IS on file but the agent ticks
                //     "not on file" and NO landline alternative resolves → there
                //     is nothing to verify against, so this behaves like the DOB
                //     row: FAIL only, Pass stays fail-closed (no bypass). The
                //     #656 Pass exception (1) is reserved for a genuinely-blank
                //     mobile; keeping this in lock-step with
                //     `landlineNotOnFilePassable` so the gate and the rendered
                //     `disablePass` can never drift.
                const mobileOnFile = hasMandatoryOnFile('mobile');
                const landlineNotOnFile =
                  showMobileAlt && !hasValue && !mobileOnFile;
                const mobileOnFileNoAlt =
                  showMobileAlt && !hasValue && mobileOnFile;
                const dobNotOnFile = m.key === 'dob' && !questions?.Dob;
                // #774 (@dylan219): the mobile row on Open24/Business24/VISA is
                // always markable Pass/Fail (like an externalCheck row), even
                // with no mobile on file. With the tick-box suppressed above,
                // `showMobileAlt`/`landlineNotOnFile`/`mobileOnFileNoAlt` are all
                // false for this row, so both `disablePass` and `disableFail`
                // collapse to `baseDisabled` (enabled once `dataReady`).
                const rowAlwaysMarkable =
                  isExternal || (mobileAlwaysMarkable && m.key === 'mobile');
                const baseDisabled =
                  !dataReady || (!rowAlwaysMarkable && !hasValue);
                const disablePass =
                  dobNotOnFile || mobileOnFileNoAlt
                    ? true
                    : landlineNotOnFile
                      ? !dataReady
                      : baseDisabled;
                const disableFail =
                  dobNotOnFile || landlineNotOnFile || mobileOnFileNoAlt
                    ? !dataReady
                    : baseDisabled;
                return (
                  <React.Fragment key={m.key}>
                    {idx > 0 && <div className="border-t-2 border-[#dad7d3]" />}
                    <QuestionRow
                      label={m.label}
                      value={
                        isExternal
                          ? undefined
                          : // Issue #372/#657: when a "not on file" toggle is ticked
                            // the verification alternative (products list / landline)
                            // is rendered in the prompt block below, so suppress the
                            // inline value to avoid duplicating it — the row stays
                            // markable Pass/Fail via `hasValue` above, and the
                            // ORIGINAL question label stays visible (never
                            // overwritten).
                            showMaidenAlt || showMobileAlt
                            ? undefined
                            : hasValue
                              ? value
                              : 'Not on file'
                      }
                      hint={m.hint}
                      name={`mandatory-${m.key}`}
                      status={mandatoryStatus[m.key] ?? null}
                      onChange={(v) => setMandatory(m.key, v)}
                      disabled={baseDisabled}
                      disablePass={disablePass}
                      disableFail={disableFail}
                    />
                    {isMaiden && (
                      <div className="mt-2">
                        <Checkbox
                          id="maiden-not-on-file"
                          checked={maidenNotOnFile}
                          onCheckedChange={(v) => {
                            setMaidenNotOnFile(v === true);
                            setMandatoryStatus((s) => ({ ...s, maiden: null }));
                          }}
                        />
                        <label
                          htmlFor="maiden-not-on-file"
                          className="ml-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
                        >
                          Mother&apos;s maiden name not on file
                        </label>
                      </div>
                    )}
                    {/* When "Mother's maiden name not on file" is ticked, prompt
                      the agent with the customer's other products/accounts so
                      they can ask "Do you have any other products or accounts
                      with PTSB?". Issue #389 (mirroring the security screens):
                      when the REAL gateway account list is available it is
                      shown — masked, e.g. "Switch Current A/C ••4701" — under
                      "Confirm the following products/accounts held with the
                      customer:", identical to `SecurityScreen.tsx`. When the
                      gateway returns no accounts the prompt falls back to the
                      static bullet list (Savings Account / Mortgage / Credit
                      Card / Loan — #361), so the agent always sees a prompt and
                      the row stays markable Pass/Fail (#372, CCF-57). */}
                    {showMaidenAlt && (
                      <div
                        className="mt-3"
                        data-testid="maiden-products-prompt"
                      >
                        {/* #657: the ALTERNATIVE question the agent now asks, shown
                          beneath (not in place of) the original "Mother's maiden
                          name" question above, which stays visible. */}
                        <h4 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
                          Alternative question: Do you have any other products
                          or accounts with PTSB?
                        </h4>
                        {otherProducts && otherProducts.length > 0 ? (
                          <>
                            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-2">
                              Confirm the following products/accounts held with
                              the customer:
                            </p>
                            <ul className="list-none m-0 p-0 space-y-1">
                              {otherProducts.map((p, i) => (
                                <li
                                  key={`${p.AccountLast4}-${i}`}
                                  className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                                >
                                  {formatProductEntry(p)}
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : (
                          <>
                            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-2">
                              Customer&apos;s related products/accounts:
                            </p>
                            <ul className="grid grid-cols-2 gap-x-8 gap-y-2 list-disc pl-6 m-0">
                              {MAIDEN_FALLBACK_PRODUCTS.map((product) => (
                                <li
                                  key={product}
                                  className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]"
                                >
                                  {product}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                    {isMobile && (
                      <div className="mt-2">
                        <Checkbox
                          id="mobile-not-on-file"
                          checked={mobileNotOnFile}
                          onCheckedChange={(v) => {
                            setMobileNotOnFile(v === true);
                            setMandatoryStatus((s) => ({ ...s, mobile: null }));
                          }}
                        />
                        <label
                          htmlFor="mobile-not-on-file"
                          className="ml-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
                        >
                          Mobile number not on file
                        </label>
                      </div>
                    )}
                    {/* #657: when "Mobile number not on file" is ticked, show the
                      ALTERNATIVE question ("Can you confirm your landline
                      number?") plus the landline on file as its own labelled
                      sub-block BELOW the checkbox — the original "Mobile number"
                      question above stays visible, never overwritten. The
                      landline value drives the same fail-closed Pass/Fail gate on
                      the original row (via `mandatoryValue`), so a customer with
                      no landline on file leaves the row un-markable. */}
                    {showMobileAlt && (
                      <div
                        className="mt-3"
                        data-testid="mobile-landline-prompt"
                      >
                        <h4 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
                          Alternative question: Can you confirm your landline
                          number?
                        </h4>
                        <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                          {open24Alternative('mobile') ?? 'Not on file'}
                        </p>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </InfoBox>
        </div>

        {/* Additional security questions (any N of M). For Open24 these are the
            confirmatory Visa-debit-card + joint-accounts checks (none strictly
            required; a Fail refers to branch).

            Issue #651 (supervisor screenshot): the section header is NOT a
            separate "Additional security questions" / "Additional checks"
            heading anymore — the INSTRUCTION line itself is a dark badge bar
            (matching the Security screens) rendered ABOVE the white info box.
            The leading count sentence ("Select any N of M.") is bold-white and
            the remainder ("Check Hogan for all answers.") is grey. When no
            explicit `additionalInstructions` is configured a default of the same
            shape is computed per page so every registration screen is
            consistent. */}
        {config.additional.length > 0 &&
          (() => {
            const instruction =
              config.additionalInstructions ??
              (config.open24Extras
                ? 'Ask the customer the following and mark each Pass or Fail. If yes, confirm against the value(s) shown; if no, that is the correct answer.'
                : `Select any ${config.requiredAdditional} of ${config.additional.length}. Check Hogan for all answers.`);
            const { count, rest } = splitInstruction(instruction);
            return (
              <div className="space-y-3">
                <div className="bg-[#1D252C] px-6 py-3 inline-block">
                  <h2 className="text-[16px] font-['Arial',sans-serif] m-0">
                    <span className="font-bold text-white">{count}</span>
                    {rest && (
                      <span className="font-normal text-[#9ca3af]">
                        {' '}
                        {rest}
                      </span>
                    )}
                  </h2>
                </div>
                <InfoBox>
                  {config.additionalAnswersUnordered && (
                    // Issue #305 (Dylan's refined requirement, 2026-07-09) /#311:
                    // the on-file answers are free text from `customer/info` with
                    // no guaranteed order or 1:1 mapping to the questions below, so
                    // they are shown ONCE, as a plain list, rather than under a
                    // specific question.
                    <div className="mb-4">
                      <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0 mb-1">
                        Answers on file (free text — order not guaranteed):
                      </p>
                      {questions?.AdditionalQuestionAnswers?.length ? (
                        <ul className="list-disc pl-5 space-y-1">
                          {questions.AdditionalQuestionAnswers.map((a, i) => (
                            <li
                              key={i}
                              className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                            >
                              {a}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                          Not on file
                        </p>
                      )}
                    </div>
                  )}
                  <div className="space-y-6">
                    {config.additional.map((q, idx) => {
                      // The on-file answer (if any) — an additional question can only
                      // be marked Pass/Fail when the gateway returned an answer to
                      // verify against (CCF-60: all answers are displayed). Questions
                      // with no server-backed answer are disabled and labelled, so an
                      // agent can never mark a blank question Pass.
                      //
                      // Business24 (#305) / Broker24 (#311)
                      // (`additionalAnswersUnordered`): there is no per-question
                      // answer to resolve — the shared free-text list above is the
                      // only on-file signal. `hasAnswer` requires AT LEAST
                      // `requiredAdditional` DISTINCT on-file answers, not just one
                      // — both branches independently hardened this the same way
                      // in response to ai-review (MEDIUM, #305: "Single answer
                      // enables all questions" / "Duplicate answers satisfy
                      // two-answer gate"; HIGH, #311: identical finding for
                      // Broker24's mandatory checks): a single free-text item, or
                      // duplicate content like "Blue, Blue" (only 1 DISTINCT fact),
                      // must not enable marking `requiredAdditional` rows (or
                      // Broker24's mandatory eye-colour/first-school checks) Pass.
                      // The raw (non-deduplicated) list is still shown to the agent
                      // above.
                      // Open24: override `questions.JointAccounts` with the
                      // authoritative, real-gateway-backed joint-accounts source
                      // fetched above (`/security/joint-associations`, #318) — the
                      // `/security/questions` `JointAccounts` field is carried only by
                      // the mock-only `customer/security-questions` op (#76). The
                      // active-Visa-debit list, by contrast, now comes DIRECTLY from
                      // the server-enriched `questions.ActiveVisaDebitCards`
                      // (`activeVisaDebitCards` = that field, fail-closed on
                      // `ActiveVisaDebitCardsError`): #345/#388 gave
                      // `getSecurityQuestions` a real `card/list` enrichment, so this
                      // field is now live-gateway-backed and — unlike the old
                      // MEDIUM-gated client `/cards` fetch (#348) — is available on
                      // the level-`none` registration screen, where `/cards` 403'd and
                      // left the dropdown permanently empty (#407).
                      const mergedForOpen24: SecurityQuestionsData | undefined =
                        questions
                          ? config.open24Extras
                            ? {
                                ...questions,
                                JointAccounts: jointAccounts,
                                ActiveVisaDebitCards: activeVisaDebitCards,
                              }
                            : questions
                          : undefined;
                      const answerValue = config.additionalAnswersUnordered
                        ? undefined
                        : q.answer && mergedForOpen24
                          ? q.answer(mergedForOpen24)
                          : undefined;
                      // #348: a list-valued resolver (e.g. joint accounts, active
                      // Visa debit cards) renders each entry as its own list item
                      // (see `QuestionRow`'s `listValue`) instead of one
                      // run-together string.
                      const answerListValue =
                        !config.additionalAnswersUnordered &&
                        q.answerList &&
                        mergedForOpen24
                          ? q.answerList(mergedForOpen24)
                          : undefined;
                      const hasAnswerList = !!answerListValue?.length;
                      // Mortgage (CCF-59, issue #319): the 6 questions have NO API
                      // integration at all — the agent verifies them against Hogan
                      // directly (mirroring the Mortgage Security fix, #309) — so an
                      // `externalCheck` question has no on-file answer to gate on
                      // and is always available to mark, exactly like the
                      // `MandatoryItem.externalCheck` VISA/TSYS checks above.
                      const hasAnswer = config.additionalAnswersUnordered
                        ? // Business24 (#648, @dylan219's Option A): once ticked, the
                          // question is always markable — the agent verbally verifies
                          // against the free-text answers-on-file list — superseding
                          // the #305 "≥2 distinct on-file answers" gate that left the
                          // controls disabled whenever `customer/info` was empty (the
                          // reported symptom). The "no Pass off thin data" concern is
                          // handled by the select-EXACTLY-2 requirement + verbal
                          // verification instead. Broker24 (#311/#349) leaves
                          // `additionalAlwaysMarkable` unset and KEEPS the gate.
                          config.additionalAlwaysMarkable
                          ? true
                          : countDistinctAnswers(
                              questions?.AdditionalQuestionAnswers,
                            ) >= config.requiredAdditional
                        : q.externalCheck
                          ? true
                          : q.answerList
                            ? hasAnswerList
                            : !!answerValue;
                      // Open24 (issue #42): the confirmatory checks (Visa-debit /
                      // joint-accounts) are NONE-mandatory (`requiredAdditional: 0`)
                      // and a Fail only refers to branch — they never, on their own,
                      // produce a Pass. So the agent must be able to mark them whether
                      // or not a value is on file (the answer is confirmed verbally);
                      // the Pass/Fail control stays usable once a customer is in
                      // context. This does NOT relax the fail-closed identity gate,
                      // which still requires every MANDATORY item to be on-file +
                      // Pass. Other registration variants keep the stricter
                      // answer-on-file rule for their security questions.
                      const rowDisabled = config.open24Extras
                        ? !dataReady
                        : !dataReady || !hasAnswer;

                      // Mortgage (issue #350): a tick-box in front of EACH question;
                      // the agent selects EXACTLY `selectLimit` (any 3 of 6). Only a
                      // ticked question's Pass/Fail control is shown; once the limit
                      // is reached the remaining (unticked) tick-boxes are disabled
                      // so the agent cannot select more than the limit. The overall
                      // outcome passes only when all selected questions are Pass.
                      if (selectLimit != null) {
                        const isSelected = selectedAdditionalKeys.includes(
                          q.key,
                        );
                        const limitReached =
                          selectedAdditionalKeys.length >= selectLimit;
                        return (
                          <React.Fragment key={q.key}>
                            {idx > 0 && (
                              <div className="border-t-2 border-[#dad7d3]" />
                            )}
                            <div className="flex items-center gap-3">
                              <Checkbox
                                id={`additional-select-${q.key}`}
                                checked={isSelected}
                                onCheckedChange={(v) =>
                                  toggleAdditional(q.key, v === true)
                                }
                                disabled={
                                  !dataReady || (!isSelected && limitReached)
                                }
                              />
                              <label
                                htmlFor={`additional-select-${q.key}`}
                                className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
                              >
                                {q.label}
                              </label>
                            </div>
                            {q.hint && (
                              <p className="ml-9 text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0">
                                {q.hint}
                              </p>
                            )}
                            {isSelected && (
                              <div className="ml-9 mt-3">
                                <PassFailRadios
                                  name={`additional-${q.key}`}
                                  value={additionalStatus[q.key] ?? null}
                                  onChange={(v) => setAdditional(q.key, v)}
                                  disabled={rowDisabled}
                                />
                              </div>
                            )}
                          </React.Fragment>
                        );
                      }

                      // Broker24 (issue #349): a tick-box selects the ONE question
                      // the agent is asking; only that question's Pass/Fail control
                      // is shown, and only while dataReady + an on-file answer
                      // exists to verify against.
                      if (config.additionalSelectable) {
                        const isSelected = selectedAdditionalKey === q.key;
                        return (
                          <React.Fragment key={q.key}>
                            {idx > 0 && (
                              <div className="border-t-2 border-[#dad7d3]" />
                            )}
                            <div className="flex items-center gap-3">
                              <Checkbox
                                id={`additional-select-${q.key}`}
                                checked={isSelected}
                                onCheckedChange={(v) =>
                                  selectAdditional(q.key, v === true)
                                }
                                disabled={!dataReady}
                              />
                              <label
                                htmlFor={`additional-select-${q.key}`}
                                className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
                              >
                                {q.label}
                              </label>
                            </div>
                            {isSelected && (
                              <div className="ml-9 mt-3">
                                <PassFailRadios
                                  name={`additional-${q.key}`}
                                  value={additionalStatus[q.key] ?? null}
                                  onChange={(v) => setAdditional(q.key, v)}
                                  disabled={rowDisabled}
                                />
                              </div>
                            )}
                          </React.Fragment>
                        );
                      }
                      // Open24 (#367): render an `answerDropdown` question (the
                      // active-Visa-debit-card check) as a SELECT of the last-4
                      // masked entries for the agent to verify against, faithful to
                      // the `update-reg-eds` Figma — instead of the bullet list /
                      // "Answer not on file" text. The confirmatory Pass/Fail control
                      // sits beside it and stays usable (none-mandatory, #42). An
                      // empty list still renders the (disabled) placeholder-only
                      // dropdown, so the affordance the agent expects is always shown.
                      if (q.answerDropdown) {
                        const entries = answerListValue ?? [];
                        const selectId = `additional-dropdown-${q.key}`;
                        return (
                          <React.Fragment key={q.key}>
                            {idx > 0 && (
                              <div className="border-t-2 border-[#dad7d3]" />
                            )}
                            <div className="flex items-start gap-4">
                              <div className="flex flex-col">
                                <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                                  {q.label}
                                </h3>
                                {q.hint && (
                                  <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0 mb-1">
                                    {q.hint}
                                  </p>
                                )}
                                <label htmlFor={selectId} className="sr-only">
                                  {q.answerDropdownPlaceholder ?? q.label}
                                </label>
                                <select
                                  id={selectId}
                                  value={additionalDropdownChoice[q.key] ?? ''}
                                  disabled={!dataReady || entries.length === 0}
                                  onChange={(e) =>
                                    setAdditionalDropdownChoice((s) => ({
                                      ...s,
                                      [q.key]: e.target.value,
                                    }))
                                  }
                                  className="mt-1 px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white border-[#dad7d3] hover:border-[#979c9f] focus:outline-none focus:ring-4 focus:border-[#005fcc] focus:ring-[#005fcc]/20 min-w-[250px] disabled:bg-[#f3f4f6] disabled:text-[#6b7280] disabled:cursor-not-allowed"
                                >
                                  <option value="">
                                    {q.answerDropdownPlaceholder ?? 'Select…'}
                                  </option>
                                  {entries.map((entry) => (
                                    <option key={entry} value={entry}>
                                      {entry}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="ml-auto mt-5">
                                <PassFailRadios
                                  name={`additional-${q.key}`}
                                  value={additionalStatus[q.key] ?? null}
                                  onChange={(v) => setAdditional(q.key, v)}
                                  disabled={rowDisabled}
                                />
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      }

                      return (
                        <React.Fragment key={q.key}>
                          {idx > 0 && (
                            <div className="border-t-2 border-[#dad7d3]" />
                          )}
                          <QuestionRow
                            label={q.label}
                            value={
                              config.additionalAnswersUnordered ||
                              q.externalCheck ||
                              q.answerList
                                ? undefined
                                : hasAnswer
                                  ? answerValue
                                  : 'Answer not on file'
                            }
                            listValue={
                              q.answerList
                                ? hasAnswerList
                                  ? answerListValue
                                  : // Issue #476: distinguish a GENUINELY EMPTY (but
                                    // defined) list — the authoritative source loaded
                                    // and returned no entries — from an
                                    // absent/unavailable one. The former reads the
                                    // question's `answerListEmptyLabel` (e.g. "No
                                    // joint accounts"); the latter keeps the default
                                    // "Answer not on file".
                                    answerListValue !== undefined &&
                                      q.answerListEmptyLabel
                                    ? [q.answerListEmptyLabel]
                                    : ['Answer not on file']
                                : undefined
                            }
                            hint={q.hint}
                            name={`additional-${q.key}`}
                            status={additionalStatus[q.key] ?? null}
                            onChange={(v) => setAdditional(q.key, v)}
                            disabled={rowDisabled}
                          />
                        </React.Fragment>
                      );
                    })}
                  </div>
                  {/* #846 (CCRS-3940): screens WITHOUT 2FA (Broker24, SCRN-21)
                      have no 2FA box to host the overall PASS/FAIL flag, so it
                      never rendered. Per the Broker24 Figma ("Pass/Fail Flags for
                      Box 2") the flag sits at the bottom of the additional-
                      questions box, before the final scripting. Driven by the
                      SAME `securityPass` / `showFailureBanner` state as the 2FA
                      screens' flag (FAIL still waits until every question is
                      answered — #781 / CCF-318). 2FA screens keep their single
                      flag inside the 2FA box — never both. */}
                  {!config.twoFactor && (securityPass || showFailureBanner) && (
                    <div className="flex items-center gap-4 flex-wrap mt-4">
                      {securityPass && (
                        <span
                          className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#15803d] text-white border-2 border-[#15803d]"
                          aria-label="Verification passed"
                        >
                          PASS
                        </span>
                      )}
                      {showFailureBanner && (
                        <span
                          className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#dc2626] text-white border-2 border-[#dc2626]"
                          aria-label="Verification failed"
                        >
                          FAIL
                        </span>
                      )}
                    </div>
                  )}
                </InfoBox>
              </div>
            );
          })()}

        {/* 2FA OTP step. */}
        {config.twoFactor && (
          <InfoBox>
            <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-4">
              Two-factor authentication (2FA)
            </h2>
            {/* CCF-16 read-back scripting: the agent reads the script with
                the customer's first name and confirms the full mobile number
                before sending the code, then asks the customer to read the
                code back. Issue #631 (dylan219): apply the same 2FA scripting
                to Mortgage, Visa and Business registration journeys as the
                Open24 registration page. */}
            <>
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-4">
                Thanks {(customer.name ?? '').trim().split(/\s+/)[0] || 'there'}
                , I need to do one more security check. I&apos;ll send a code to
                your mobile phone. Can you tell me your phone number please?
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-4">
                Customer mobile number:{' '}
                <span className="font-normal">
                  {formatMobileNational(questions?.MobileNumber) ??
                    questions?.MaskedMobile ??
                    'Not on file'}
                </span>
              </p>
            </>
            {/* #737 (@dylan219's #412 rule): when the SERVER reported that
                2FA was already satisfied for THIS customer earlier in this
                session (a validated OTP bound to it, or a consumed sanctioned
                #534 skip — never a client claim, invalidated by a #639
                lockout), do NOT make the agent redo it: show the same
                "already completed" indicator the medium→high carry of #456
                uses, in place of the OTP entry + skip controls. Fail-closed:
                the interactive step stays until the server confirms. */}
            {priorTwoFaSatisfied ? (
              <p
                role="status"
                className="text-[16px] font-['Arial',sans-serif] font-bold text-[#15803d] mt-0 mb-0"
              >
                2FA already completed on this call.
              </p>
            ) : (
              <>
                {/* #534 — the "reason for non-completion" (skip-2FA) dropdown,
                ECL AGENTS ONLY (tester ruling): the SAME field as the Security
                screens (#287), reusing the shared reason list. Selecting a
                reason records the skip server-side and treats the 2FA piece as
                passed; clearing it restores the OTP requirement. */}
                {canSkip2fa && (
                  <div className="mb-4 rounded-md border-2 border-[#dad7d3] bg-[#faf9f7] p-4">
                    <label
                      htmlFor="reg-skip-2fa-reason"
                      className="block text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                    >
                      Customer cannot complete 2FA — select a reason to skip:
                    </label>
                    <select
                      id="reg-skip-2fa-reason"
                      value={skip2faReason}
                      disabled={
                        !dataReady ||
                        otpStatus === 'pass' ||
                        otpStatus === 'fail' ||
                        skip2faRecording
                      }
                      onChange={(e) => void handleSkip2faSelect(e.target.value)}
                      aria-label="Reason for non-completion of two-factor authentication"
                      className="w-full max-w-[420px] h-[44px] px-3 border-2 border-[#dad7d3] rounded text-[16px] font-['Arial',sans-serif] text-[#1d252c] bg-white disabled:bg-[#f3f4f6] disabled:text-[#6b7280]"
                    >
                      <option value="">— Do not skip 2FA —</option>
                      {registrationSkip2faReasons.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                    {/* #586 extension (@dylan219): the "2FA Exemption Approved
                        by TL" reason requires the approving team lead's name —
                        the SAME field, validation and gating as the High
                        Security screen (#586). The skip is NOT recorded (and
                        the 2FA piece NOT treated as passed) until a valid name
                        is entered and the skip is submitted; the name is
                        re-validated and captured server-side in the audited
                        skip record. */}
                    {exemptionApprovedByTl && !skip2faRecorded && (
                      <div className="mt-3">
                        <label
                          htmlFor="reg-skip-2fa-team-lead-name"
                          className="block text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                        >
                          Team lead&apos;s name
                        </label>
                        <input
                          id="reg-skip-2fa-team-lead-name"
                          type="text"
                          value={teamLeadName}
                          maxLength={40}
                          disabled={
                            !dataReady ||
                            otpStatus === 'pass' ||
                            otpStatus === 'fail' ||
                            skip2faRecording
                          }
                          aria-invalid={teamLeadNameError !== ''}
                          aria-describedby={
                            teamLeadNameError
                              ? 'reg-skip-2fa-team-lead-name-error'
                              : undefined
                          }
                          onChange={(e) => {
                            const v = e.target.value;
                            setTeamLeadName(v);
                            setTeamLeadNameError(
                              v.trim() && !TL_NAME_ALLOWED.test(v.trim())
                                ? "Team lead's name may contain only letters, spaces, apostrophes and hyphens."
                                : '',
                            );
                          }}
                          className="w-full max-w-[420px] h-[44px] px-3 border-2 border-[#dad7d3] rounded text-[16px] font-['Arial',sans-serif] text-[#1d252c] bg-white disabled:bg-[#f3f4f6] disabled:text-[#6b7280]"
                        />
                        {teamLeadNameError && (
                          <p
                            id="reg-skip-2fa-team-lead-name-error"
                            role="alert"
                            className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-2 mb-0"
                          >
                            {teamLeadNameError}
                          </p>
                        )}
                        <Button
                          type="button"
                          onClick={() => void handleTlExemptionConfirm()}
                          disabled={
                            !teamLeadNameValid ||
                            skip2faRecording ||
                            !dataReady ||
                            otpStatus === 'pass' ||
                            otpStatus === 'fail'
                          }
                          className="mt-3 bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                        >
                          Record 2FA skip
                        </Button>
                      </div>
                    )}
                    {/* #789: the "2FA Customer Device Issue" reason requires the
                        agent to confirm troubleshooting was completed with the
                        customer before the skip is recorded. Mirrors the deferred
                        TL-exemption flow — not recorded until the box is ticked
                        and the skip submitted. */}
                    {requiresTroubleshootingConfirmation(skip2faReason) &&
                      !skip2faRecorded && (
                        <div className="mt-3">
                          <label
                            htmlFor="reg-skip-2fa-troubleshooting-done"
                            className="flex items-center gap-2 text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                          >
                            <input
                              id="reg-skip-2fa-troubleshooting-done"
                              type="checkbox"
                              checked={troubleshootingDone}
                              disabled={
                                !dataReady ||
                                otpStatus === 'pass' ||
                                otpStatus === 'fail' ||
                                skip2faRecording
                              }
                              onChange={(e) =>
                                setTroubleshootingDone(e.target.checked)
                              }
                              className="h-5 w-5"
                            />
                            Troubleshooting completed
                          </label>
                          <Button
                            type="button"
                            onClick={() => void handleTroubleshootingConfirm()}
                            disabled={
                              !troubleshootingDone ||
                              skip2faRecording ||
                              !dataReady ||
                              otpStatus === 'pass' ||
                              otpStatus === 'fail'
                            }
                            className="mt-3 bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                          >
                            Record 2FA skip
                          </Button>
                        </div>
                      )}
                    {skip2faRecorded && (
                      <p
                        role="status"
                        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#15803d] mt-3 mb-0"
                      >
                        2FA skipped — reason recorded: {skip2faReason}.
                      </p>
                    )}
                    {skip2faError && (
                      <p
                        role="alert"
                        className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                      >
                        {skip2faError}
                      </p>
                    )}
                  </div>
                )}
                <div className="mb-4 flex items-center gap-4 flex-wrap">
                  <Button
                    type="button"
                    onClick={() => void handleGenerateOtp()}
                    disabled={
                      generating ||
                      otpStatus === 'pass' ||
                      !dataReady ||
                      skip2faRecorded ||
                      // #760: no on-file mobile ⇒ no SMS OTP can be sent.
                      !hasMobileOnFile ||
                      // #774: a mobile-question FAIL already determines a 2FA
                      // fail, so there is no point sending an OTP.
                      twoFactorAutoFail
                    }
                    aria-label="Generate one-time passcode"
                    className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                  >
                    {generating
                      ? 'Generating…'
                      : otpSent
                        ? 'Resend OTP'
                        : 'Send Code'}
                  </Button>
                  {/* #774 (@dylan219): the Mortgage "Unable to complete 2FA"
                  tick box, BESIDE the Send Code button. Ticking it records
                  the 2FA FAIL server-side (the run seam — never a client
                  claim, the #729 lesson) and disables the OTP path; the fail
                  outcome/messaging stays DEFERRED until every other question
                  is completed (the #781 deferral in showFailureBanner).
                  #774 reopen (@dylan219): the box is for the agents who run
                  mortgage registration — every non-ECL mortgage role sees it
                  (`canFail2fa`: config flag + the SERVER-resolved session role
                  loaded and NOT ECL, fail-closed hidden while `/session` is
                  still loading — the !1134 convention). The recording routes
                  are gated by the dedicated `recordRegistration2faFail`
                  permission (every SCRN-20 role EXCEPT ECLAgent; ECL keeps
                  ONLY the #534 skip dropdown above, byte-for-byte unchanged —
                  "On ECL, please leave this exactly as it is"). No other
                  registration screen sets the flag, so every sibling screen
                  stays exactly as it is. */}
                  {canFail2fa && (
                    <label className="flex items-center gap-2 text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                      <input
                        type="checkbox"
                        checked={unable2faTicked}
                        disabled={
                          !dataReady ||
                          unable2faRecording ||
                          otpStatus === 'pass' ||
                          skip2faRecorded
                        }
                        onChange={(e) =>
                          void handleUnable2faToggle(e.target.checked)
                        }
                        className="h-5 w-5"
                      />
                      Unable to complete 2FA
                    </label>
                  )}
                  {unable2faError && (
                    <p
                      role="alert"
                      className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                    >
                      {unable2faError}
                    </p>
                  )}
                </div>
                {/* Send Code (generate) failure — gateway code + message shown
                INLINE directly under the Send Code button (#782), so the error
                is obvious right where the agent clicked. Validate failures
                render under the Validate button instead (below). */}
                {otpError && otpError.source === 'generate' && (
                  <p
                    role="alert"
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                  >
                    {otpError.code
                      ? `(${otpError.code}) ${otpError.message}`
                      : otpError.message}
                  </p>
                )}
                {otpSent && (
                  <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-4">
                    I just sent the code to your phone. When you get it, can you
                    read it back to me?
                  </p>
                )}
                <label
                  htmlFor="reg-otp-code"
                  className="block text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2"
                >
                  One-time passcode
                </label>
                <div className="flex items-center gap-4 flex-wrap">
                  <input
                    id="reg-otp-code"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    value={otpCode}
                    disabled={
                      !otpSent ||
                      otpStatus === 'pass' ||
                      skip2faRecorded ||
                      // #774: mobile-question FAIL auto-fails 2FA — no OTP entry.
                      twoFactorAutoFail
                    }
                    onChange={(e) =>
                      setOtpCode(
                        e.target.value.replace(/[^0-9]/g, '').slice(0, 6),
                      )
                    }
                    style={{ width: '220px' }}
                    className="px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#6b7280] transition-all focus:outline-none focus:ring-4 focus:border-[#005fcc] focus:ring-[#005fcc]/20 disabled:bg-[#f3f4f6] disabled:text-[#6b7280] disabled:cursor-not-allowed hover:border-[#979c9f] border-[#dad7d3]"
                  />
                  <Button
                    type="button"
                    onClick={() => void handleValidateOtp()}
                    disabled={
                      !otpSent ||
                      otpCode.length !== 6 ||
                      validating ||
                      otpStatus === 'pass' ||
                      skip2faRecorded ||
                      // #774: mobile-question FAIL auto-fails 2FA — no OTP entry.
                      twoFactorAutoFail
                    }
                    aria-label="Validate one-time passcode"
                    className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                  >
                    {validating ? 'Validating…' : 'Validate'}
                  </Button>
                </div>
                {/* Validate failure — a wrong (non-final) OTP code: gateway code
                + message shown INLINE directly under the Validate button (#782),
                where the agent just clicked, so it is obvious. Stays retryable.
                The terminal blocked branch renders the FAIL banner above. */}
                {otpError && otpError.source === 'validate' && (
                  <p
                    role="alert"
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                  >
                    {otpError.code
                      ? `Incorrect code (${otpError.code}): ${otpError.message}`
                      : otpError.message}
                  </p>
                )}
                {otpAttemptsRemaining != null &&
                  otpStatus !== 'pass' &&
                  otpStatus !== 'fail' &&
                  otpSent && (
                    <p
                      role="status"
                      aria-live="polite"
                      className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mt-2 mb-0"
                    >
                      {`OTP attempts remaining: ${otpAttemptsRemaining}`}
                    </p>
                  )}
              </>
            )}
            {/* Overall security outcome (CCF-57/59/60): the flag reflects the
                WHOLE registration verification — PASS only when every mandatory
                + additional question AND 2FA pass, FAIL if any Pass/Fail
                question fails or 2FA fails. It must not represent the 2FA
                result alone. The fail is deferred until 2FA resolves on
                two-factor journeys (via showFailureBanner), per CCF-57.
                Rendered OUTSIDE the #737 branch so the outcome shows in BOTH
                the OTP-entry and the already-completed states (mirrors
                `SecurityScreen.tsx` #456, where the PASS/FAIL outcome shows in
                both the carry-indicator and OTP-entry branches). */}
            <div className="flex items-center gap-4 flex-wrap">
              {securityPass && (
                <span
                  className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#15803d] text-white border-2 border-[#15803d]"
                  aria-label="Verification passed"
                >
                  PASS
                </span>
              )}
              {showFailureBanner && (
                <span
                  className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#dc2626] text-white border-2 border-[#dc2626]"
                  aria-label="Verification failed"
                >
                  FAIL
                </span>
              )}
            </div>
          </InfoBox>
        )}

        {/* Branch-referral guidance when any security question fails. */}
        {showFailureBanner && (
          <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
            <img
              src={infoIcon}
              alt=""
              aria-hidden="true"
              className="w-5 h-5 mt-1 shrink-0"
            />
            <div>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
                {config.failureText ?? (
                  <>
                    Sorry, the information you gave us does not match our
                    records. You will need to visit a branch with a photo ID and
                    your account details.
                  </>
                )}
              </p>
              {!config.hideReferralNote && (
                <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                  {config.failureNote ??
                    'Please leave a note that the customer has been referred to branch.'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Mandatory scripting + transfer, once security passes. */}
        {securityPass && !transferred && (
          <InfoBox>
            <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-4">
              Mandatory scripting
            </h2>
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-6 leading-[1.6]">
              {mandatoryScript || MANDATORY_SCRIPT}
            </p>
            <div className="flex items-center gap-3 mb-6">
              <Checkbox
                id="scripting-complete"
                checked={scriptingComplete}
                onCheckedChange={(v) => setScriptingComplete(v === true)}
              />
              <label
                htmlFor="scripting-complete"
                className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] select-none cursor-pointer"
              >
                Scripting complete
              </label>
            </div>
            <div className="flex gap-4 flex-wrap">
              <Button
                type="button"
                onClick={() => void handleTransfer()}
                disabled={!scriptingComplete || transferring}
                aria-label="Transfer to automated self-service"
                className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {transferring
                  ? 'Transferring…'
                  : 'Transfer to automated self-service'}
              </Button>
            </div>
            {transferError && (
              <p
                role="alert"
                className="mt-4 text-[14px] font-['Arial',sans-serif] text-[#dc2626]"
              >
                {transferError}
              </p>
            )}
            {config.transferNote && (
              <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mt-4 mb-0 leading-[1.5]">
                {config.transferNote}
              </p>
            )}
          </InfoBox>
        )}

        {/* #653 (@dylan219) — Reset Internet Password option. Shown ONLY once
            every registration security question (+ 2FA where the screen has one) has
            passed (`securityPass`). Completion of the questions is treated as
            sufficient security, so this navigation has no extra 2FA gate — it
            mirrors the broker security → IPW option (#568). Navigates to
            SCRN-57 with a scoped Back button that returns here with every answer
            + the Pass intact. */}
        {securityPass && config.ipwReset && currentRegistrationRoute && (
          <InfoBox>
            <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-4">
              Reset internet password
            </h2>
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-6 leading-[1.6]">
              Security is complete. You can reset this customer&apos;s internet
              password now — you&apos;ll be able to return to this registration
              screen with all answers intact.
            </p>
            <div className="flex gap-4 flex-wrap">
              <Button
                type="button"
                onClick={() => void handleResetInternetPassword()}
                disabled={ipwNavigating}
                aria-label="Generate manual password"
                className="bg-white text-[#1d252c] border-2 border-[#1d252c] hover:bg-[#f3f4f6] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {/* #757 (@philipc39) — button relabelled from "Reset internet
                    password" to "Generate manual password" across all
                    registration screens. Navigation/behaviour unchanged. */}
                {ipwNavigating ? 'Opening…' : 'Generate manual password'}
              </Button>
            </div>
            {ipwNavError && (
              <p
                role="alert"
                className="mt-4 text-[14px] font-['Arial',sans-serif] text-[#dc2626]"
              >
                {ipwNavError}
              </p>
            )}
          </InfoBox>
        )}

        {/* Transfer confirmation. */}
        {transferred && (
          <div
            role="status"
            className="bg-[#d4edda] border-2 border-[#28a745] p-6 shadow-sm"
          >
            <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
              Customer transferred to automated self-service.
            </p>
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
              The customer will choose their personal access number and receive
              a temporary internet password via the IVR.
            </p>
          </div>
        )}
      </div>

      <div className="mt-8 flex items-center gap-6 flex-wrap">
        <Button
          type="button"
          onClick={() => navigate('/idv')}
          aria-label="Back to ID&V"
          className="bg-white text-[#1d252c] border-2 border-[#1d252c] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
        >
          Back to ID&amp;V
        </Button>
        {/* Registration-page switcher / process menu (#163, @dylan219): lets the
            agent switch to a different registration type when they have landed on
            the default Open24 page (an unregistered card has no resolvable
            customer segment). */}
        <RegistrationSwitcher screenCode={config.screenCode} />
      </div>

      {/* CCF-43 (issue #666) — Customer Authentication Hold Check pop-up. Raised
          when the agent lands on this Registration security screen for a
          customer who has an access-restriction hold, exactly as on the
          Dashboard. The check fails OPEN (no pop-up) when it cannot be
          completed; only a returned hold surfaces this modal. */}
      {authHold && authHoldOpen && (
        <AuthHoldPopup result={authHold} onClose={closeAuthHold} />
      )}

      {/* #669 / #666 — J & N Hold Handling. A found verdict is published by
          `useAuthHoldCheck` to the app-shell navigation-lock store and the
          NON-DISMISSIBLE warning is rendered by App.tsx in place of the routed
          screens on every route — there is deliberately NO pop-up rendered here
          anymore (no acknowledge, no navigation off it — @dylan219,
          2026-09-01). */}
    </main>
  );
}
